import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";
import FichaResumenPanel from "../components/propiedades/FichaResumenPanel";
import { cargarDatosFichaResumen } from "../components/propiedades/cargarDatosFichaResumen";

// ── Modal para crear/editar feed ───────────────────────────────────────────

const FORMATOS_XML = [
  { value: "kyero_v3", label: "Kyero XML v3", desc: "Estándar más usado en España e internacional" },
  { value: "idealista", label: "Idealista", desc: "Formato propio de Idealista" },
  { value: "fotocasa", label: "Fotocasa / Habitaclia", desc: "Formato propio de Fotocasa" },
  { value: "sooprema", label: "Web", desc: "Web Vitalcasa (WordPress / feed multi-idioma)" },
  { value: "openimmo", label: "OpenImmo", desc: "Estándar europeo (Alemania / centroeuropa)" },
  { value: "rightmove_blm", label: "Rightmove BLM", desc: "Estándar británico (UK)" },
  { value: "reso", label: "RESO / RETS", desc: "Estándar norteamericano (MLS)" },
];

/** Contador publicados [/ contratados] en el portal. */
function PortalPropCountBadge({ count, contratados, portalColor, isDark, accentFallback, compact, large, title: titleProp }) {
  const n = Number(count) || 0;
  const cMax = contratados != null && contratados !== "" ? Number(contratados) : null;
  const hasCupo = Number.isFinite(cMax) && cMax >= 0;
  const label = hasCupo
    ? `${n.toLocaleString("es-ES")}/${cMax.toLocaleString("es-ES")}`
    : n.toLocaleString("es-ES");
  const c = portalColor || accentFallback || "#3b82f6";
  const title =
    titleProp ||
    (hasCupo
      ? `${n.toLocaleString("es-ES")} publicados · ${cMax.toLocaleString("es-ES")} contratados`
      : `${n.toLocaleString("es-ES")} propiedad${n === 1 ? "" : "es"} activa${n === 1 ? "" : "s"}`);
  if (large) {
    return (
      <span
        className="inline-flex items-center justify-center shrink-0 min-w-[2.1rem] h-8 px-2 rounded-md text-sm font-bold tabular-nums leading-none"
        style={{
          color: c,
          background: isDark ? "rgba(255,255,255,0.07)" : "#ffffff",
          border: `1.5px solid ${c}`,
          boxShadow: isDark ? "0 1px 4px rgba(0,0,0,0.2)" : `0 1px 3px ${c}28`,
        }}
        title={title}
      >
        {label}
      </span>
    );
  }
  if (compact) {
    return (
      <span
        className="inline-flex items-center justify-center shrink-0 min-w-[1.35rem] h-5 px-1 rounded-md text-[9px] font-bold tabular-nums leading-none"
        style={{
          color: c,
          background: isDark ? "rgba(255,255,255,0.07)" : "#ffffff",
          border: `1px solid ${c}`,
          boxShadow: isDark ? "0 1px 4px rgba(0,0,0,0.2)" : `0 1px 3px ${c}28`,
        }}
        title={title}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 shrink-0 h-7 pl-2 pr-2.5 rounded-lg text-xs font-bold tabular-nums"
      style={{
        color: c,
        background: isDark ? "rgba(255,255,255,0.07)" : "#ffffff",
        border: `1.5px solid ${c}`,
        boxShadow: isDark ? `0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)` : `0 1px 4px ${c}30`,
      }}
      title={title}
    >
      {label}
      <span
        className="text-[9px] font-semibold uppercase tracking-wide"
        style={{ opacity: 0.72, fontWeight: 700 }}
      >
        {hasCupo ? "pub/ctr" : "inm."}
      </span>
    </span>
  );
}

function CopyUrlButton({ text, theme, title = "Copiar al portapapeles" }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [copied, setCopied] = useState(false);
  const expectedRef = useRef("");
  const pollRef = useRef(null);
  const maxTimerRef = useRef(null);

  const stopWatching = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  }, []);

  const resetCopied = useCallback(() => {
    stopWatching();
    expectedRef.current = "";
    setCopied(false);
  }, [stopWatching]);

  const checkClipboard = useCallback(async () => {
    if (!expectedRef.current) return;
    try {
      const current = await navigator.clipboard.readText();
      if (current !== expectedRef.current) resetCopied();
    } catch {
      /* Sin permiso de lectura: el timeout máximo resetea el estado */
    }
  }, [resetCopied]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      expectedRef.current = text;
      setCopied(true);
      stopWatching();

      pollRef.current = setInterval(() => { void checkClipboard(); }, 350);
      maxTimerRef.current = setTimeout(resetCopied, 12000);

      const onFocus = () => { void checkClipboard(); };
      window.addEventListener("focus", onFocus, { once: true });
    } catch {
      resetCopied();
    }
  };

  useEffect(() => () => resetCopied(), [resetCopied]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 inline-flex items-center justify-center gap-1 text-[10px] px-1.5 py-0.5 rounded min-w-[3.25rem] transition-colors"
      style={{
        color: copied ? (isDark ? "#4ade80" : "#15803d") : t.accentBlue,
        border: `1px solid ${copied ? (isDark ? "#166534" : "#86efac") : t.border}`,
        background: copied ? (isDark ? "rgba(34,197,94,0.12)" : "#f0fdf4") : "transparent",
      }}
      title={copied ? "Copiado al portapapeles" : title}
      aria-label={copied ? "Copiado" : title}
    >
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20 6L9 17l-5-5"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        "Copiar"
      )}
    </button>
  );
}

function ModalFeed({ feed, portales, portalesStats = {}, onClose, onSave, theme, puede }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const puedeEditar = feed ? puede("cfg_portales_editar", "modificar") : puede("cfg_portales_nuevo", "alta");
  const modFormato = puede("cfg_portales_formato", "modificar");
  const ce = parseFeedConfigExtra(feed);
  const [form, setForm] = useState({
    nombre: feed?.nombre || "",
    portal_id: feed?.portal_id || "",
    url_feed: feed?.url_feed || "",
    api_key: feed?.api_key || "",
    api_secret: feed?.api_secret || "",
    formato: feed?.formato || "xml",
    formato_xml: feed?.formato_xml || "kyero_v3",
    activo: feed?.activo !== false,
    incluir_datos_privados: ce.incluir_datos_privados === true,
    sooprema_auto_sync: ce.sooprema_auto_sync === true,
    sooprema_import_path: ce.sooprema_import_path || "",
    sooprema_api_base: ce.sooprema_api_base || "",
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const cfg = parseFeedConfigExtra(feed);
    setForm({
      nombre: feed?.nombre || "",
      portal_id: feed?.portal_id || "",
      url_feed: feed?.url_feed || "",
      api_key: feed?.api_key || "",
      api_secret: feed?.api_secret || "",
      formato: feed?.formato || "xml",
      formato_xml: feed?.formato_xml || "kyero_v3",
      activo: feed?.activo !== false,
      incluir_datos_privados: cfg.incluir_datos_privados === true,
      sooprema_auto_sync: cfg.sooprema_auto_sync === true,
      sooprema_import_path: cfg.sooprema_import_path || "",
      sooprema_api_base: cfg.sooprema_api_base || "",
    });
  }, [feed?.id, feed?.nombre, feed?.url_feed, feed?.formato_xml, feed?.config_extra, feed?.activo, feed?.portal_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const prevCfg = parseFeedConfigExtra(feed);
      const {
        incluir_datos_privados,
        sooprema_auto_sync,
        sooprema_import_path,
        sooprema_api_base,
        ...restForm
      } = form;
      await onSave({
        ...restForm,
        modo: "export",
        _id: feed?.id || null,
        portal_id: form.portal_id ? parseInt(form.portal_id) : null,
        config_extra: {
          ...prevCfg,
          incluir_datos_privados: !!incluir_datos_privados,
          sooprema_auto_sync: form.formato_xml === "sooprema" ? !!sooprema_auto_sync : false,
          sooprema_import_path: form.formato_xml === "sooprema" ? String(sooprema_import_path || "").trim() || null : null,
          sooprema_api_base: form.formato_xml === "sooprema" ? String(sooprema_api_base || "").trim() || null : null,
        },
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = { background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: t.overlayBg }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl shadow-2xl" style={{ background: t.bgCard, border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${t.border}` }}>
          <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>{feed ? "Editar feed de exportación" : "Nuevo feed de exportación"}</h3>
          <button onClick={onClose} className="text-xl" style={{ color: t.textMuted }}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-sm px-3 py-2 rounded-lg" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" }}>
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Nombre del feed</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Feed Kyero, Feed colaborador X..."
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar este feed" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>URL web (opcional)</label>
            <input value={form.url_feed} onChange={e => setForm({ ...form, url_feed: e.target.value })}
              placeholder="https://www.tuweb.com/ficha-ejemplo..."
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar este feed" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
            <p className="text-[10px] mt-1 leading-snug" style={{ color: t.textMuted }}>
              Para Fotocasa/Idealista la <strong style={{ color: t.textSecondary }}>URL de la pasarela</strong> es la que muestra la tabla como <strong style={{ color: t.textSecondary }}>XML</strong> (termina en <code className="text-[9px]">/api/portales/feeds/…/xml</code>): cópiala y pégala en el panel del portal. Este campo es solo referencia web si la quieres anotar aquí.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
              Portal asociado <span className="normal-case font-normal">(opcional)</span>
            </label>
            <select value={form.portal_id} onChange={e => setForm({ ...form, portal_id: e.target.value })}
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar este feed" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">Sin portal asociado</option>
              {portales.map(p => {
                const cn = portalesStats?.[p.id]?.total_props;
                const suf = cn != null ? ` · ${Number(cn).toLocaleString("es-ES")} inm.` : "";
                return (
                  <option key={p.id} value={p.id}>{p.nombre}{suf}</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Formato XML</label>
            <select value={form.formato_xml} onChange={e => setForm({ ...form, formato_xml: e.target.value })}
              disabled={!puedeEditar || !modFormato} title={(!puedeEditar || !modFormato) ? "No tienes permiso para modificar el formato XML" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              {FORMATOS_XML.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1" style={{ color: t.textMuted }}>
              {FORMATOS_XML.find(f => f.value === form.formato_xml)?.desc}
            </p>
          </div>
          <div className="rounded-lg px-3 py-3 space-y-2" style={{ background: isDark ? t.bgPanel : "#f8fafc", border: `1px solid ${t.border}` }}>
            <label className="flex items-start gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
              <input
                type="checkbox"
                checked={form.incluir_datos_privados}
                onChange={e => setForm({ ...form, incluir_datos_privados: e.target.checked })}
                disabled={!puedeEditar}
                className="rounded mt-0.5"
              />
              <span>
                <strong style={{ color: t.textPrimary }}>Incluir datos privados</strong>
                <span className="block text-[10px] mt-0.5 leading-snug" style={{ color: t.textMuted }}>
                  Dirección completa, precio propietario, honorarios y datos del propietario. El precio de venta siempre se exporta.
                  Desactiva esta opción para feeds a colaboradores.
                </span>
              </span>
            </label>
          </div>
          {form.formato_xml === "sooprema" && (
            <div className="rounded-lg px-3 py-3 space-y-2" style={{ background: isDark ? "#0f172a" : "#eff6ff", border: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}` }}>
              <p className="text-[11px] leading-snug" style={{ color: t.textSecondary }}>
                <strong>Web Vitalcasa:</strong> la web WordPress consume
                <code className="mx-1">POST /api/public/web/v1/properties</code>
                (HMAC). El tick «Web» en la matriz y «Publicar en web» en ficha son el mismo interruptor.
                El feed XML (formato técnico) sigue en la columna XML si hace falta.
              </p>
              <p className="text-[10px] leading-snug" style={{ color: t.textMuted }}>
                Opcional legado: push a un importador externo (ruta REST). No es el camino habitual de la web.
              </p>
              <label className="flex items-start gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
                <input
                  type="checkbox"
                  checked={form.sooprema_auto_sync}
                  onChange={e => setForm({ ...form, sooprema_auto_sync: e.target.checked })}
                  className="rounded mt-0.5"
                  disabled={!form.sooprema_import_path?.trim() || !puedeEditar}
                />
                <span>
                  <strong style={{ color: t.textPrimary }}>Push automático (legado)</strong>
                  <span className="block text-[10px] mt-0.5 leading-snug" style={{ color: t.textMuted }}>
                    Solo si hay ruta API configurada. Al activar una propiedad en el feed, VitalCasa intenta enviarla a Sooprema.
                  </span>
                </span>
              </label>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <div>
                  <label className="block text-[10px] font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Base API (opcional)</label>
                  <input value={form.sooprema_api_base} onChange={e => setForm({ ...form, sooprema_api_base: e.target.value })}
                    placeholder="https://api.sooprema.com o URL que indique Sooprema"
                    disabled={!puedeEditar}
                    className="w-full px-2 py-1.5 rounded text-xs font-mono" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Ruta API importación (opcional)</label>
                  <input value={form.sooprema_import_path} onChange={e => setForm({ ...form, sooprema_import_path: e.target.value })}
                    placeholder="/v2/... (la que os dé soporte Sooprema)"
                    disabled={!puedeEditar}
                    className="w-full px-2 py-1.5 rounded text-xs font-mono" style={inputStyle} />
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                API Key {form.formato_xml === "sooprema" ? <span className="normal-case font-normal">(Web HMAC public / legado)</span> : <span className="normal-case font-normal">(opcional)</span>}
              </label>
              <input value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}
                placeholder={form.formato_xml === "sooprema" ? "HMAC publicKey (web) o clave legado..." : "Usuario o código si el portal protege el feed..."}
                disabled={!puedeEditar}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>API Secret <span className="normal-case font-normal">(opcional)</span></label>
              <input value={form.api_secret} onChange={e => setForm({ ...form, api_secret: e.target.value })} type="password"
                placeholder="Contraseña del feed, si aplica..."
                disabled={!puedeEditar}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
            </div>
          </div>
          <p className="text-[10px] -mt-2 leading-snug" style={{ color: t.textMuted }}>
            Idealista/Fotocasa a veces generan credenciales para que <em>ellos</em> descarguen tu XML; si VitalCasa tuviera que autenticarse al publicar, iría aquí (hoy el XML es público por URL salvo que añadas protección en servidor).
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase" style={{ color: t.textMuted }}>Formato:</label>
              <select value={form.formato} onChange={e => setForm({ ...form, formato: e.target.value })}
                disabled={!puedeEditar}
                className="px-3 py-1.5 rounded-lg text-sm" style={inputStyle}>
                <option value="xml">XML</option>
                <option value="json">JSON</option>
                <option value="api">API REST</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
              <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} disabled={!puedeEditar} className="rounded" />
              Activo
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
              style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}>Cancelar</button>
            <button type="submit" disabled={guardando || !puedeEditar} title={!puedeEditar ? "No tienes permiso para guardar este feed" : undefined} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: t.accentBlue, opacity: (guardando || !puedeEditar) ? 0.6 : 1 }}>
              {guardando ? "Guardando..." : (feed ? "Actualizar" : "Crear feed")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function parseFeedConfigExtra(f) {
  let c = f?.config_extra;
  if (c == null) return {};
  if (typeof c === "string") {
    try {
      c = JSON.parse(c);
    } catch {
      return {};
    }
  }
  return typeof c === "object" && c && !Array.isArray(c) ? c : {};
}

function ModalFeedImport({ feed, onClose, onSave, theme, puede }) {
  const t = theme.colors;
  const puedeEditar = feed ? puede("cfg_portales_editar", "modificar") : puede("cfg_portales_nuevo", "alta");
  const modFormato = puede("cfg_portales_formato", "modificar");
  const ce = parseFeedConfigExtra(feed);
  const [form, setForm] = useState({
    nombre: feed?.nombre || "",
    url_feed: feed?.url_feed || "",
    api_key: feed?.api_key || "",
    api_secret: feed?.api_secret || "",
    formato: "xml",
    formato_xml: feed?.formato_xml || "kyero_v3",
    activo: feed?.activo !== false,
    import_agente_id: ce.import_agente_id || ce.agente_import_id || "",
  });
  const [agentes, setAgentes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const cfg = parseFeedConfigExtra(feed);
    setForm({
      nombre: feed?.nombre || "",
      url_feed: feed?.url_feed || "",
      api_key: feed?.api_key || "",
      api_secret: feed?.api_secret || "",
      formato: "xml",
      formato_xml: feed?.formato_xml || "kyero_v3",
      activo: feed?.activo !== false,
      import_agente_id: cfg.import_agente_id || cfg.agente_import_id || "",
    });
  }, [feed?.id, feed?.nombre, feed?.url_feed, feed?.formato_xml, feed?.config_extra, feed?.activo]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/agentes");
        const list = r.data?.data ?? r.data ?? [];
        setAgentes(Array.isArray(list) ? list : []);
      } catch {
        setAgentes([]);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const prevCfg = parseFeedConfigExtra(feed);
      const importAgente = String(form.import_agente_id || "").trim();
      await onSave({
        nombre: form.nombre,
        url_feed: form.url_feed,
        api_key: form.api_key,
        api_secret: form.api_secret,
        formato: form.formato,
        formato_xml: form.formato_xml,
        activo: form.activo,
        modo: "import",
        portal_id: null,
        config_extra: {
          ...prevCfg,
          import_agente_id: importAgente || null,
        },
        _id: feed?.id || null,
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = { background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: t.overlayBg }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl shadow-2xl" style={{ background: t.bgCard, border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${t.border}` }}>
          <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>{feed ? "Editar importación" : "Importar desde CRM antiguo"}</h3>
          <button type="button" onClick={onClose} className="text-xl" style={{ color: t.textMuted }}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-sm px-3 py-2 rounded-lg" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" }}>
              {error}
            </div>
          )}
          <p className="text-xs leading-relaxed" style={{ color: t.textMuted }}>
            Indica la URL pública del XML que genera tu CRM anterior. VitalCasa descargará ese XML, interpretará el formato que elijas y creará o actualizará propiedades por <strong style={{ color: t.textSecondary }}>referencia</strong>.
          </p>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Nombre</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Import Kyero antiguo"
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar esta importación" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>URL del XML (CRM antiguo)</label>
            <input value={form.url_feed} onChange={e => setForm({ ...form, url_feed: e.target.value })}
              placeholder="https://..."
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar esta importación" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} required />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Formato XML</label>
            <select value={form.formato_xml} onChange={e => setForm({ ...form, formato_xml: e.target.value })}
              disabled={!puedeEditar || !modFormato} title={(!puedeEditar || !modFormato) ? "No tienes permiso para modificar el formato XML" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              {FORMATOS_XML.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1" style={{ color: t.textMuted }}>
              {FORMATOS_XML.find(f => f.value === form.formato_xml)?.desc}
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Agente por defecto <span className="normal-case font-normal">(si el XML no trae comercial)</span></label>
            <select value={form.import_agente_id} onChange={e => setForm({ ...form, import_agente_id: e.target.value })}
              disabled={!puedeEditar} title={!puedeEditar ? "No tienes permiso para editar esta importación" : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">— Sin asignar automáticamente —</option>
              {agentes.map(a => (
                <option key={a.id} value={a.id}>{a.nombre}{a.email ? ` (${a.email})` : ""}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1" style={{ color: t.textMuted }}>
              Con Sooprema, si el XML incluye nombre o email del comercial, se intentará enlazar con un agente del CRM (coincidencia de email o nombre). Si no hay datos o no coincide, se usará el agente elegido aquí.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>API Key <span className="normal-case font-normal">(opcional)</span></label>
              <input value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}
                disabled={!puedeEditar}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>API Secret <span className="normal-case font-normal">(opcional)</span></label>
              <input value={form.api_secret} onChange={e => setForm({ ...form, api_secret: e.target.value })} type="password"
                disabled={!puedeEditar}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
            <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} disabled={!puedeEditar} className="rounded" />
            Activo
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
              style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}>Cancelar</button>
            <button type="submit" disabled={guardando || !puedeEditar} title={!puedeEditar ? "No tienes permiso para guardar esta importación" : undefined} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: t.accentBlue, opacity: (guardando || !puedeEditar) ? 0.6 : 1 }}>
              {guardando ? "Guardando..." : (feed ? "Actualizar" : "Crear importación")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal para crear/editar portal ─────────────────────────────────────────

function labelExtraPorSlug(slug) {
  const s = String(slug || "").toLowerCase();
  if (s === "fotocasa") return "X-Source";
  if (s === "idealista") return "ILC / Feed key";
  if (s === "pisos") return "Licence / Agency ID";
  return "Extra (header / id)";
}

function ModalPortal({ portal, onClose, onSave, theme }) {
  const t = theme.colors;
  const [form, setForm] = useState({
    nombre: portal?.nombre || "",
    slug: portal?.slug || "",
    tipo: portal?.tipo || "feed",
    color: portal?.color || "#2563eb",
    url_base: portal?.url_base || "",
    url_anuncios: portal?.url_anuncios || "",
    activo: portal?.activo !== false,
    anuncios_contratados:
      portal?.anuncios_contratados != null && portal?.anuncios_contratados !== ""
        ? String(portal.anuncios_contratados)
        : "",
    anuncios_ocupados_base:
      portal?.anuncios_ocupados_base != null && portal?.anuncios_ocupados_base !== ""
        ? String(portal.anuncios_ocupados_base)
        : "",
    api_key: portal?.api_key || "",
    api_secret: portal?.api_secret || "",
    api_extra: portal?.api_extra || "",
    feed_url: portal?.feed_url || "",
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const esApi = form.tipo === "api";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const payload = {
        ...form,
        anuncios_contratados:
          form.anuncios_contratados === "" || form.anuncios_contratados == null
            ? null
            : Math.max(0, parseInt(form.anuncios_contratados, 10) || 0),
        anuncios_ocupados_base:
          form.anuncios_ocupados_base === "" || form.anuncios_ocupados_base == null
            ? null
            : Math.max(0, parseInt(form.anuncios_ocupados_base, 10) || 0),
        _id: portal?.id || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = { background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: t.overlayBg }} onClick={onClose}>
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl" style={{ background: t.bgCard, border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10" style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}>
          <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>{portal ? "Editar portal" : "Nuevo portal"}</h3>
          <button onClick={onClose} className="text-xl" style={{ color: t.textMuted }}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-sm px-3 py-2 rounded-lg" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" }}>
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Nombre</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre del portal..."
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Tipo conexión</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="feed">Feed XML</option>
                <option value="api">API / Pasarela</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Color</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border-0" />
                <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
              URL de nuestros anuncios
            </label>
            <input
              value={form.url_anuncios}
              onChange={(e) => setForm({ ...form, url_anuncios: e.target.value })}
              placeholder="https://www.idealista.com/pro/vitalcasa/…"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            />
            <p className="text-[10px] mt-1.5 leading-snug" style={{ color: t.textMuted }}>
              Página pública de Vitalcasa en este portal (se abre desde la tabla).
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>Web del portal</label>
            <input value={form.url_base} onChange={e => setForm({ ...form, url_base: e.target.value })}
              placeholder="https://www.portal.com"
              className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>

          <div className="rounded-lg p-3 space-y-3" style={{ border: `1px solid ${t.border}`, background: t.bgPanel || t.bgCard }}>
            <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: t.textMuted }}>
              {esApi ? "Credenciales API" : "Feed / conexión"}
            </p>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                {esApi ? "API Key / Client ID" : "API Key del feed (si aplica)"}
              </label>
              <input
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder={esApi ? "Api-Key o client id…" : "Clave HTTP del feed…"}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                style={inputStyle}
                autoComplete="off"
              />
            </div>
            {esApi && (
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                  Secret / Client secret
                </label>
                <input
                  value={form.api_secret}
                  onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                  placeholder="Opcional (Idealista, agency id…)"
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  style={inputStyle}
                  autoComplete="off"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                {esApi ? labelExtraPorSlug(form.slug || portal?.slug) : "Notas / extra"}
              </label>
              <input
                value={form.api_extra}
                onChange={(e) => setForm({ ...form, api_extra: e.target.value })}
                placeholder={esApi ? "X-Source, ILC, licence…" : "Opcional"}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                style={inputStyle}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                {esApi ? "URL API (override, opcional)" : "URL del feed XML"}
              </label>
              <input
                value={form.feed_url}
                onChange={(e) => setForm({ ...form, feed_url: e.target.value })}
                placeholder={esApi ? "https://imports.gw.fotocasa.pro" : "https://…/xml"}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                style={inputStyle}
              />
              <p className="text-[10px] mt-1.5 leading-snug" style={{ color: t.textMuted }}>
                {esApi
                  ? "Si está vacío, se usan las variables de Railway. Si rellenas Api-Key aquí, tiene prioridad sobre el env."
                  : "Referencia del feed. El XML de exportación detallado sigue en la sección de abajo (Feeds XML)."}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                Anuncios contratados
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.anuncios_contratados}
                onChange={(e) => setForm({ ...form, anuncios_contratados: e.target.value })}
                placeholder="Ej: 60"
                className="w-full px-3 py-2 rounded-lg text-sm tabular-nums"
                style={inputStyle}
              />
              <p className="text-[10px] mt-1.5 leading-snug" style={{ color: t.textMuted }}>
                Cupo máximo contratado (p. ej. 60 en Idealista).
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: t.textMuted }}>
                Ya ocupados en el portal
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.anuncios_ocupados_base}
                onChange={(e) => setForm({ ...form, anuncios_ocupados_base: e.target.value })}
                placeholder="Ej: 50"
                className="w-full px-3 py-2 rounded-lg text-sm tabular-nums"
                style={inputStyle}
              />
              <p className="text-[10px] mt-1.5 leading-snug" style={{ color: t.textMuted }}>
                Si el área privada muestra 50/60, pon 50. El CRM avisará al intentar pasar del hueco libre.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
            <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} className="rounded" />
            Portal activo (aparece en la matriz de propiedades)
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
              style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}>Cancelar</button>
            <button type="submit" disabled={guardando} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: t.accentBlue, opacity: guardando ? 0.6 : 1 }}>
              {guardando ? "Guardando..." : (portal ? "Actualizar" : "Crear portal")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function fmtFechaAlta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function filtrosFeedDefaults(esImport, saved = {}) {
  return {
    buscar: saved.buscar || "",
    referencia: saved.referencia || "",
    tipo: saved.tipo || "",
    poblacion: saved.poblacion || "",
    operacion: saved.operacion || "",
    precio_min: saved.precio_min ?? "",
    precio_max: saved.precio_max ?? "",
    habitaciones_min: saved.habitaciones_min ?? "",
    banos_min: saved.banos_min ?? "",
    solo_activas: saved.solo_activas === true,
    solo_en_feed: saved.solo_en_feed === true || esImport,
    fecha_modo: saved.fecha_modo || "",
    fecha_alta_desde: saved.fecha_alta_desde || "",
    fecha_alta_hasta: saved.fecha_alta_hasta || "",
  };
}

function filtrosParaApi(filtros, sortCol, sortDir) {
  const out = { ...filtros };
  if (sortCol) {
    out.sort_col = sortCol;
    out.sort_dir = sortDir;
  }
  return out;
}

// ── Modal: propiedades de un feed sin portal ───────────────────────────────

function ModalFeedPropiedades({ feed, theme, onClose, onUpdated }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const esImport = (feed.modo || "export") === "import";
  const ceInicial = parseFeedConfigExtra(feed);
  const [seleccionAutomatica, setSeleccionAutomatica] = useState(ceInicial.seleccion_automatica === true);
  const [propiedades, setPropiedades] = useState([]);
  const [matriz, setMatriz] = useState({});
  const [filtros, setFiltros] = useState(() => filtrosFeedDefaults(esImport, ceInicial.filtros_auto));
  const [sortCol, setSortCol] = useState(ceInicial.filtros_auto?.sort_col || null);
  const [sortDir, setSortDir] = useState(ceInicial.filtros_auto?.sort_dir || "desc");
  const [opcionesFiltros, setOpcionesFiltros] = useState({ tipos: [], poblaciones: [], operaciones: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [enFeedTotal, setEnFeedTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [guardandoAuto, setGuardandoAuto] = useState(false);
  const debRef = useRef(null);
  const saveAutoRef = useRef(null);
  const refreshAutoRef = useRef(null);
  const lastSavedFiltrosRef = useRef(JSON.stringify(filtrosParaApi(
    filtrosFeedDefaults(esImport, ceInicial.filtros_auto),
    ceInicial.filtros_auto?.sort_col || null,
    ceInicial.filtros_auto?.sort_dir || "desc"
  )));
  const lastSavedAutoRef = useRef(ceInicial.seleccion_automatica === true);
  const feedRef = useRef(feed);
  const onUpdatedRef = useRef(onUpdated);
  const cargarRef = useRef(null);
  feedRef.current = feed;
  onUpdatedRef.current = onUpdated;

  const imgBase = window.location.hostname === "localhost" ? "http://localhost:4000" : "https://vitalcasa-crm-production.up.railway.app";

  useEffect(() => {
    api.get("/portales/filtros").then(r => setOpcionesFiltros(r.data)).catch(() => {});
  }, []);

  const guardarConfigAuto = useCallback(async (auto, filtrosActuales, sortC, sortD, { notifyParent = false, force = false, silent = false } = {}) => {
    if (esImport) return;
    const payload = filtrosParaApi(filtrosActuales, sortC, sortD);
    const payloadKey = JSON.stringify(payload);
    if (!force && payloadKey === lastSavedFiltrosRef.current && auto === lastSavedAutoRef.current) {
      return;
    }
    if (!silent) setGuardandoAuto(true);
    try {
      const f = feedRef.current;
      const prevCfg = parseFeedConfigExtra(f);
      await api.put(`/portales/feeds/${f.id}`, {
        nombre: f.nombre,
        portal_id: f.portal_id || null,
        url_feed: f.url_feed || "",
        api_key: f.api_key || "",
        api_secret: f.api_secret || "",
        formato: f.formato || "xml",
        formato_xml: f.formato_xml || "kyero_v3",
        activo: f.activo !== false,
        modo: f.modo || "export",
        config_extra: {
          ...prevCfg,
          seleccion_automatica: !!auto,
          filtros_auto: payload,
        },
      });
      lastSavedFiltrosRef.current = payloadKey;
      lastSavedAutoRef.current = auto;
      if (notifyParent) onUpdatedRef.current?.();
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setGuardandoAuto(false);
    }
  }, [esImport]);

  const cargar = useCallback(async (pagina = 1) => {
    setCargando(true);
    try {
      const params = new URLSearchParams({ page: pagina, limit: pageSize });
      params.set("solo_activas", filtros.solo_activas ? "true" : "false");
      Object.entries(filtros).forEach(([k, v]) => {
        if (k === "solo_activas") return;
        if (k === "solo_en_feed") {
          if (v === true) params.set("solo_en_feed", "true");
          return;
        }
        if (k === "fecha_modo") {
          if (v) params.set("fecha_modo", v);
          return;
        }
        if (v && v !== "" && v !== false) params.set(k, v);
      });
      if (sortCol) {
        params.set("sort_col", sortCol);
        params.set("sort_dir", sortDir);
      }
      const res = await api.get(`/portales/feeds/${feed.id}/propiedades-matriz?${params}`);
      const rows = res.data.data || [];
      const m = res.data.matriz || {};
      setPropiedades(rows);
      setMatriz(m);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pagina);
      setEnFeedTotal(res.data.en_feed_total ?? (res.data.seleccion_automatica ? 0 : Object.values(m).filter(Boolean).length));
      if (typeof res.data.seleccion_automatica === "boolean") {
        setSeleccionAutomatica(res.data.seleccion_automatica);
        lastSavedAutoRef.current = res.data.seleccion_automatica;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, [feed.id, filtros, pageSize, sortCol, sortDir]);

  cargarRef.current = cargar;

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => cargar(1), 300);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [filtros, pageSize, sortCol, sortDir, cargar]);

  useEffect(() => {
    if (!seleccionAutomatica || esImport) return;
    if (saveAutoRef.current) clearTimeout(saveAutoRef.current);
    saveAutoRef.current = setTimeout(() => {
      guardarConfigAuto(true, filtros, sortCol, sortDir, { silent: true });
    }, 2000);
    return () => { if (saveAutoRef.current) clearTimeout(saveAutoRef.current); };
  }, [filtros, sortCol, sortDir, seleccionAutomatica, esImport, guardarConfigAuto]);

  useEffect(() => {
    if (!seleccionAutomatica || esImport) return;
    refreshAutoRef.current = setInterval(() => cargarRef.current?.(page), 5 * 60 * 1000);
    return () => { if (refreshAutoRef.current) clearInterval(refreshAutoRef.current); };
  }, [seleccionAutomatica, esImport, page]);

  const toggleSort = (colId) => {
    if (sortCol === colId) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(colId); setSortDir("asc"); }
  };

  const handleToggleAuto = async (checked) => {
    setSeleccionAutomatica(checked);
    await guardarConfigAuto(checked, filtros, sortCol, sortDir, { notifyParent: true, force: true });
    cargar(1);
  };

  const handleToggle = async (propId) => {
    const key = String(propId);
    const currentVal = matriz[key] || matriz[propId] || false;
    const newVal = !currentVal;
    setToggling(key);
    setMatriz(prev => ({ ...prev, [propId]: newVal }));
    try {
      await api.post(`/portales/feeds/${feed.id}/propiedades-toggle`, { propiedad_id: propId, activo: newVal });
      setEnFeedTotal(prev => prev + (newVal ? 1 : -1));
      onUpdated?.();
    } catch (e) {
      setMatriz(prev => ({ ...prev, [propId]: currentVal }));
      console.error(e);
    } finally {
      setToggling(null);
    }
  };

  const handleToggleCol = async (activar) => {
    const ids = propiedades.map(p => p.id);
    if (ids.length === 0) return;
    const prev = { ...matriz };
    const next = { ...matriz };
    ids.forEach(id => { next[id] = activar; });
    setMatriz(next);
    try {
      await api.post(`/portales/feeds/${feed.id}/propiedades-bulk`, { propiedad_ids: ids, activo: activar });
      onUpdated?.();
      cargar(page);
    } catch (e) {
      setMatriz(prev);
      console.error(e);
    }
  };

  const limpiarFiltros = () => {
    setFiltros(filtrosFeedDefaults(esImport));
    setSortCol(null);
    setSortDir("desc");
  };

  const hayFiltros = Object.entries(filtros).some(([k, v]) => {
    if (k === "solo_activas" || k === "solo_en_feed") return v === true;
    if (k === "fecha_modo") return v !== "";
    return v !== "";
  });

  const SortHeader = ({ colId, label, align = "left" }) => {
    const isActive = sortCol === colId;
    return (
      <th
        className={`px-2 py-2 font-bold uppercase text-[10px] select-none group ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}
        style={{ color: t.textMuted, cursor: "pointer" }}
        onClick={() => toggleSort(colId)}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start" }}>
          {label}
          <span style={{ opacity: isActive ? 1 : 0, transition: "opacity 0.15s", fontSize: 10, color: isActive ? t.accentBlue : t.textMuted }}
            className="group-hover:opacity-100">
            {isActive ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </span>
      </th>
    );
  };

  const inputStyle = { background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.textPrimary };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: t.overlayBg }} onClick={onClose}>
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col rounded-xl shadow-2xl"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div className="pr-4 min-w-0">
            <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>
              {esImport ? "Propiedades de esta importación" : "Agregar o quitar propiedades"}
            </h3>
            <p className="text-xs mt-0.5 truncate" style={{ color: t.textMuted }}>
              {feed.nombre || "Feed #" + feed.id}
              {!esImport && (seleccionAutomatica || enFeedTotal > 0) && (
                <span className="ml-2 font-semibold" style={{ color: t.accentGreen }}>
                  {enFeedTotal.toLocaleString("es-ES")} en el feed{seleccionAutomatica ? " (automático)" : ""}
                </span>
              )}
              {guardandoAuto && (
                <span className="ml-2" style={{ color: t.textMuted }}>· guardando filtros…</span>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-xl shrink-0 px-2" style={{ color: t.textMuted }}>✕</button>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Panel filtros (estilo Sooprema) */}
          <div className="w-44 shrink-0 overflow-y-auto px-3 py-3 space-y-2" style={{ borderRight: `1px solid ${t.border}`, background: isDark ? t.bgPanel : "#f8fafc" }}>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Buscar</label>
              <input value={filtros.buscar} onChange={e => setFiltros({ ...filtros, buscar: e.target.value })}
                placeholder="Ref, tipo, población…"
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Tipo</label>
              <select value={filtros.tipo} onChange={e => setFiltros({ ...filtros, tipo: e.target.value })}
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle}>
                <option value="">Todos</option>
                {opcionesFiltros.tipos.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Población</label>
              <select value={filtros.poblacion} onChange={e => setFiltros({ ...filtros, poblacion: e.target.value })}
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle}>
                <option value="">Todas</option>
                {opcionesFiltros.poblaciones.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Hab.</label>
              <input type="number" min="0" value={filtros.habitaciones_min} onChange={e => setFiltros({ ...filtros, habitaciones_min: e.target.value })}
                placeholder="Mín."
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Baños</label>
              <input type="number" min="0" value={filtros.banos_min} onChange={e => setFiltros({ ...filtros, banos_min: e.target.value })}
                placeholder="Mín."
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Referencia</label>
              <input value={filtros.referencia} onChange={e => setFiltros({ ...filtros, referencia: e.target.value })}
                placeholder="3479-C"
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Precio min</label>
              <input type="number" value={filtros.precio_min} onChange={e => setFiltros({ ...filtros, precio_min: e.target.value })}
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Precio max</label>
              <input type="number" value={filtros.precio_max} onChange={e => setFiltros({ ...filtros, precio_max: e.target.value })}
                className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-0.5" style={{ color: t.textMuted }}>Fecha alta</label>
              <select value={filtros.fecha_modo} onChange={e => setFiltros({ ...filtros, fecha_modo: e.target.value, fecha_alta_desde: "", fecha_alta_hasta: "" })}
                className="w-full px-2 py-1.5 rounded text-xs mb-1" style={inputStyle}>
                <option value="">Sin filtro</option>
                <option value="desde">A partir de</option>
                <option value="rango">Entre fechas</option>
              </select>
              {filtros.fecha_modo === "desde" && (
                <input type="date" value={filtros.fecha_alta_desde}
                  onChange={e => setFiltros({ ...filtros, fecha_alta_desde: e.target.value })}
                  className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
              )}
              {filtros.fecha_modo === "rango" && (
                <div className="space-y-1">
                  <input type="date" value={filtros.fecha_alta_desde} title="Desde"
                    onChange={e => setFiltros({ ...filtros, fecha_alta_desde: e.target.value })}
                    className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
                  <input type="date" value={filtros.fecha_alta_hasta} title="Hasta"
                    onChange={e => setFiltros({ ...filtros, fecha_alta_hasta: e.target.value })}
                    className="w-full px-2 py-1.5 rounded text-xs" style={inputStyle} />
                </div>
              )}
            </div>
            {!esImport && (
              <label className="flex items-start gap-1.5 text-[11px] cursor-pointer pt-1 rounded px-1 py-1.5"
                style={{ color: t.textSecondary, background: seleccionAutomatica ? (isDark ? "#10b98118" : "#ecfdf5") : "transparent", border: seleccionAutomatica ? `1px solid ${t.accentGreen}44` : "1px solid transparent" }}>
                <input type="checkbox" checked={seleccionAutomatica} onChange={e => handleToggleAuto(e.target.checked)} className="rounded mt-0.5" />
                <span>
                  <span className="font-semibold block" style={{ color: seleccionAutomatica ? t.accentGreen : t.textPrimary }}>Feed automático</span>
                  <span className="text-[10px] leading-tight block mt-0.5" style={{ color: t.textMuted }}>
                    Incluye todas las disponibles que cumplan estos filtros. Las nuevas captaciones entran solas.
                  </span>
                </span>
              </label>
            )}
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer pt-1" style={{ color: t.textSecondary }}>
              <input type="checkbox" checked={filtros.solo_activas} onChange={e => setFiltros({ ...filtros, solo_activas: e.target.checked })} className="rounded" />
              Solo disponibles
            </label>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: t.textSecondary }}>
              <input type="checkbox" checked={filtros.solo_en_feed} onChange={e => setFiltros({ ...filtros, solo_en_feed: e.target.checked })} className="rounded" />
              Solo en el feed
            </label>
            {hayFiltros && (
              <button type="button" onClick={limpiarFiltros} className="w-full text-[11px] py-1 rounded" style={{ color: t.accentRed }}>
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Tabla propiedades */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 shrink-0 text-xs" style={{ borderBottom: `1px solid ${t.border}` }}>
              <span style={{ color: t.textMuted }}>
                {total.toLocaleString("es-ES")} propiedades · página {page}/{totalPages || 1}
              </span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5" style={{ color: t.textMuted }}>
                  Por página
                  <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                    className="px-1.5 py-0.5 rounded text-xs" style={inputStyle}>
                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                {!esImport && (
                  <>
                    <button type="button" onClick={() => handleToggleCol(true)} className="px-2 py-1 rounded font-semibold"
                      style={{ color: "#fff", background: t.accentGreen }}>
                      {seleccionAutomatica ? "Incluir vista" : "Activar vista"}
                    </button>
                    <button type="button" onClick={() => handleToggleCol(false)} className="px-2 py-1 rounded font-semibold"
                      style={{ color: t.accentRed, border: `1px solid ${t.border}` }}>
                      {seleccionAutomatica ? "Excluir vista" : "Desactivar vista"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {seleccionAutomatica && !esImport && (
              <div className="mx-4 mt-2 px-3 py-2 rounded-lg text-[11px] shrink-0"
                style={{ background: isDark ? "#10b98115" : "#ecfdf5", border: `1px solid ${t.accentGreen}55`, color: t.textSecondary }}>
                Modo automático: el XML incluye las propiedades <strong>disponibles</strong> que cumplan los filtros del panel izquierdo.
                Las nuevas captaciones entran solas. Puedes <strong>excluir manualmente</strong> las que quieras con el interruptor de cada fila.
              </div>
            )}

            <div className="flex-1 overflow-y-auto overflow-x-auto px-4 py-2">
              {cargando ? (
                <div className="py-16 text-center text-sm" style={{ color: t.textMuted }}>Cargando propiedades…</div>
              ) : propiedades.length === 0 ? (
                <div className="py-16 text-center text-sm leading-relaxed px-6" style={{ color: t.textMuted }}>
                  {filtros.solo_en_feed
                    ? "Ninguna propiedad en el feed con estos filtros. Desactiva «Solo en el feed» para ver todo el catálogo."
                    : "Sin resultados. Prueba otros filtros o limpia la búsqueda."}
                </div>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                      <th className="px-1 py-2 text-left font-bold uppercase text-[10px]" style={{ color: t.textMuted, width: 72 }}>Imágenes</th>
                      <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: t.textMuted }}>Tipo</th>
                      <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: t.textMuted }}>Población</th>
                      <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: t.textMuted, width: 40 }}>Hab.</th>
                      <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: t.textMuted, width: 44 }}>Baños</th>
                      <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: t.textMuted }}>Referencia</th>
                      <SortHeader colId="precio" label="Precio" align="right" />
                      <SortHeader colId="fecha_alta" label="Fecha alta" align="center" />
                      <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: t.textMuted, width: 80 }}>
                        {seleccionAutomatica ? "En feed" : "Activar"}
                        {!esImport && (
                          <div className="flex justify-center gap-1 mt-0.5 font-normal normal-case">
                            <button type="button" onClick={() => handleToggleCol(true)} className="text-[9px]" style={{ color: t.accentGreen }} title={seleccionAutomatica ? "Incluir todas en vista" : "Activar todas en vista"}>✓</button>
                            <button type="button" onClick={() => handleToggleCol(false)} className="text-[9px]" style={{ color: t.accentRed }} title={seleccionAutomatica ? "Excluir todas en vista" : "Desactivar todas en vista"}>✗</button>
                          </div>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {propiedades.map((prop, idx) => {
                      const activo = matriz[prop.id] === true;
                      const busy = toggling === String(prop.id);
                      const fotoSrc = prop.foto
                        ? (prop.foto.startsWith("http") ? prop.foto : imgBase + prop.foto)
                        : null;
                      return (
                        <tr key={prop.id}
                          style={{
                            borderTop: `1px solid ${t.border}`,
                            background: activo ? (isDark ? "#10b98112" : "#ecfdf5") : (idx % 2 === 0 ? t.bgCard : (isDark ? t.bgPanel : "#fafafa")),
                          }}>
                          <td className="px-1 py-1">
                            <div className="flex items-center gap-1">
                              {fotoSrc ? (
                                <img src={fotoSrc} alt="" className="object-cover rounded shrink-0" style={{ width: 48, height: 36 }} />
                              ) : (
                                <div className="flex items-center justify-center rounded shrink-0 text-[9px]"
                                  style={{ width: 48, height: 36, background: isDark ? t.bgPanel : "#e2e8f0", color: t.textMuted }}>—</div>
                              )}
                              <span className="text-[10px] tabular-nums shrink-0" style={{ color: t.textMuted }}>
                                ({prop.num_fotos || 0})
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 font-medium" style={{ color: t.accentBlue }}>{prop.tipo || "—"}</td>
                          <td className="px-2 py-1.5" style={{ color: t.textPrimary }}>{prop.poblacion || "—"}</td>
                          <td className="px-2 py-1.5 text-center tabular-nums" style={{ color: t.textPrimary }}>{prop.num_dormitorios ?? "—"}</td>
                          <td className="px-2 py-1.5 text-center tabular-nums" style={{ color: t.textPrimary }}>{prop.num_banos ?? "—"}</td>
                          <td className="px-2 py-1.5 font-mono font-bold" style={{ color: isDark ? "#fbbf24" : "#dc2626" }}>{prop.referencia}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-medium" style={{ color: t.textPrimary }}>
                            {prop.precio_venta ? Number(prop.precio_venta).toLocaleString("es-ES") : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-center tabular-nums whitespace-nowrap" style={{ color: t.textSecondary }}>
                            {fmtFechaAlta(prop.fecha_alta)}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button type="button" disabled={busy} onClick={() => handleToggle(prop.id)}
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors mx-auto"
                              style={{
                                background: activo ? "#10b981" : (isDark ? "#374151" : "#d1d5db"),
                                opacity: busy ? 0.5 : 1,
                              }}
                              title={activo
                                ? (seleccionAutomatica ? "Excluir del feed automático" : "Quitar del feed")
                                : (seleccionAutomatica ? "Volver a incluir en el feed automático" : "Añadir al feed")}>
                              <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                                style={{ transform: activo ? "translateX(22px)" : "translateX(4px)" }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 py-2 shrink-0" style={{ borderTop: `1px solid ${t.border}` }}>
                <button type="button" disabled={page <= 1} onClick={() => cargar(page - 1)}
                  className="px-4 py-1.5 rounded text-xs font-medium disabled:opacity-40"
                  style={{ border: `1px solid ${t.border}`, color: t.textSecondary }}>‹ Anterior</button>
                <span className="text-xs tabular-nums" style={{ color: t.textMuted }}>{page} / {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => cargar(page + 1)}
                  className="px-4 py-1.5 rounded text-xs font-medium disabled:opacity-40"
                  style={{ border: `1px solid ${t.border}`, color: t.textSecondary }}>Siguiente ›</button>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 shrink-0 flex gap-3" style={{ borderTop: `1px solid ${t.border}` }}>
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
            style={{ background: t.accentBlue, color: "#fff" }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal JSON API Fotocasa Pro ────────────────────────────────────────────

function ModalFotocasaProJson({ theme, onClose, referenciaInicial = "3610-C" }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [ref, setRef] = useState(referenciaInicial);
  const [tab, setTab] = useState("preview"); // preview | remoto
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [remoto, setRemoto] = useState(null);

  const cargarPreview = async (referencia) => {
    const r = String(referencia || ref || "").trim();
    if (!r) {
      setError("Indica una referencia");
      return;
    }
    setCargando(true);
    setError("");
    try {
      const res = await api.get(`/portales/fotocasa-pro/preview?referencia=${encodeURIComponent(r)}`);
      setPreview(res.data);
      setTab("preview");
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.error || e.message || "Error al generar JSON");
    } finally {
      setCargando(false);
    }
  };

  const cargarRemoto = async () => {
    setCargando(true);
    setError("");
    try {
      const res = await api.get("/portales/fotocasa-pro/remoto");
      setRemoto(res.data);
      setTab("remoto");
    } catch (e) {
      setRemoto(null);
      setError(e.response?.data?.error || e.message || "Error al listar remoto");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPreview(referenciaInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonText = tab === "preview"
    ? (preview ? JSON.stringify(preview.json, null, 2) : "")
    : (remoto ? JSON.stringify(remoto.data, null, 2) : "");

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      await dialogs.alert("JSON copiado al portapapeles");
    } catch {
      await dialogs.alert("No se pudo copiar");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="w-full max-w-4xl max-h-[90vh] rounded-xl flex flex-col overflow-hidden"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div>
            <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>Fotocasa Pro — JSON API</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              Agencia test: {preview?.agencia || remoto?.agencia || "TEST API VITALCASA"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm px-2 py-1 rounded" style={{ color: t.textMuted }}>✕</button>
        </div>

        <div className="px-5 py-3 flex flex-wrap items-end gap-2 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Referencia</label>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") cargarPreview(ref); }}
              placeholder="3610-C"
              className="w-full px-2.5 py-1.5 rounded-lg text-sm"
              style={{ background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary }}
            />
          </div>
          <button type="button" onClick={() => cargarPreview(ref)} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: t.accentBlue, opacity: cargando ? 0.6 : 1 }}>
            Ver JSON envío
          </button>
          <button type="button" onClick={cargarRemoto} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ border: `1px solid ${t.border}`, color: t.textSecondary }}>
            Listado remoto
          </button>
          {jsonText && (
            <button type="button" onClick={copiar}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ color: t.accentGreen }}>
              Copiar JSON
            </button>
          )}
        </div>

        <div className="px-5 pt-2 flex gap-2 shrink-0">
          <button type="button" onClick={() => setTab("preview")}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "preview" ? "#fff" : t.textSecondary,
              background: tab === "preview" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            JSON que enviamos
          </button>
          <button type="button" onClick={() => { setTab("remoto"); if (!remoto) cargarRemoto(); }}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "remoto" ? "#fff" : t.textSecondary,
              background: tab === "remoto" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            En Fotocasa ahora{remoto ? ` (${remoto.total})` : ""}
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-2 text-xs px-3 py-2 rounded" style={{ background: isDark ? "#3f1d1d" : "#fef2f2", color: t.accentRed }}>
            {error}
          </div>
        )}

        {tab === "preview" && preview && (
          <div className="px-5 pt-2 text-[11px] space-y-0.5 shrink-0" style={{ color: t.textMuted }}>
            <div>POST/PUT <span className="font-mono">{preview.base_url}/api/property</span></div>
            <div>X-Source: <span className="font-mono">{preview.headers?.["X-Source"] || "—"}</span></div>
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-3 min-h-0">
          {cargando ? (
            <p className="text-sm" style={{ color: t.textMuted }}>Cargando…</p>
          ) : (
            <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all rounded-lg p-3"
              style={{ background: isDark ? t.bgPanel : "#f8fafc", color: t.textPrimary, border: `1px solid ${t.border}` }}>
              {jsonText || "Sin datos"}
            </pre>
          )}
        </div>

        <div className="px-5 py-3 shrink-0" style={{ borderTop: `1px solid ${t.border}` }}>
          <button type="button" onClick={onClose} className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal JSON API Pisos / Habitatsoft ─────────────────────────────────────

function ModalPisosJson({ theme, onClose, referenciaInicial = "3610-C" }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [ref, setRef] = useState(referenciaInicial);
  const [tab, setTab] = useState("preview");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [remoto, setRemoto] = useState(null);

  const cargarPreview = async (referencia) => {
    const r = String(referencia || ref || "").trim();
    if (!r) {
      setError("Indica una referencia");
      return;
    }
    setCargando(true);
    setError("");
    try {
      const res = await api.get(`/portales/pisos/preview?referencia=${encodeURIComponent(r)}`);
      setPreview(res.data);
      setTab("preview");
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.error || e.message || "Error al generar JSON");
    } finally {
      setCargando(false);
    }
  };

  const cargarRemoto = async () => {
    setCargando(true);
    setError("");
    try {
      const res = await api.get("/portales/pisos/remoto");
      setRemoto(res.data);
      setTab("remoto");
    } catch (e) {
      setRemoto(null);
      setError(e.response?.data?.error || e.message || "Error al listar remoto");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPreview(referenciaInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonText = tab === "preview"
    ? (preview ? JSON.stringify(preview.json, null, 2) : "")
    : (remoto ? JSON.stringify(remoto.data, null, 2) : "");

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      await dialogs.alert("JSON copiado al portapapeles");
    } catch {
      await dialogs.alert("No se pudo copiar");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="w-full max-w-4xl max-h-[90vh] rounded-xl flex flex-col overflow-hidden"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div>
            <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>Pisos.com — Habitatsoft API</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              Licence test: {preview?.licence_id || remoto?.licence_id || "996094"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm px-2 py-1 rounded" style={{ color: t.textMuted }}>✕</button>
        </div>

        <div className="px-5 py-3 flex flex-wrap items-end gap-2 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Referencia</label>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") cargarPreview(ref); }}
              placeholder="3610-C"
              className="w-full px-2.5 py-1.5 rounded-lg text-sm"
              style={{ background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary }}
            />
          </div>
          <button type="button" onClick={() => cargarPreview(ref)} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: t.accentBlue, opacity: cargando ? 0.6 : 1 }}>
            Ver JSON envío
          </button>
          <button type="button" onClick={cargarRemoto} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ border: `1px solid ${t.border}`, color: t.textSecondary }}>
            Listado remoto
          </button>
          {jsonText && (
            <button type="button" onClick={copiar}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ color: t.accentGreen }}>
              Copiar JSON
            </button>
          )}
        </div>

        <div className="px-5 pt-2 flex gap-2 shrink-0">
          <button type="button" onClick={() => setTab("preview")}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "preview" ? "#fff" : t.textSecondary,
              background: tab === "preview" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            JSON que enviamos
          </button>
          <button type="button" onClick={() => { setTab("remoto"); if (!remoto) cargarRemoto(); }}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "remoto" ? "#fff" : t.textSecondary,
              background: tab === "remoto" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            En Habitatsoft ahora
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-2 text-xs px-3 py-2 rounded" style={{ background: isDark ? "#3f1d1d" : "#fef2f2", color: t.accentRed }}>
            {error}
          </div>
        )}

        {tab === "preview" && preview && (
          <div className="px-5 pt-2 text-[11px] space-y-0.5 shrink-0" style={{ color: t.textMuted }}>
            <div>PUT <span className="font-mono">{preview.base_url}/public/v1/licenses/{preview.licence_id}/property</span></div>
            <div>externalId = reference = <span className="font-mono">{preview.referencia}</span></div>
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-3 min-h-0">
          {cargando ? (
            <p className="text-sm" style={{ color: t.textMuted }}>Cargando…</p>
          ) : (
            <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all rounded-lg p-3"
              style={{ background: isDark ? t.bgPanel : "#f8fafc", color: t.textPrimary, border: `1px solid ${t.border}` }}>
              {jsonText || "Sin datos"}
            </pre>
          )}
        </div>

        <div className="px-5 py-3 shrink-0" style={{ borderTop: `1px solid ${t.border}` }}>
          <button type="button" onClick={onClose} className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function ModalIdealistaPartnersJson({ theme, onClose, referenciaInicial = "3610-C" }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [ref, setRef] = useState(referenciaInicial);
  const [tab, setTab] = useState("preview");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [remoto, setRemoto] = useState(null);

  const cargarPreview = async (referencia) => {
    const r = String(referencia || ref || "").trim();
    if (!r) {
      setError("Indica una referencia");
      return;
    }
    setCargando(true);
    setError("");
    try {
      const res = await api.get(`/portales/idealista-partners/preview?referencia=${encodeURIComponent(r)}`);
      setPreview(res.data);
      setTab("preview");
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.error || e.message || "Error al generar JSON");
    } finally {
      setCargando(false);
    }
  };

  const cargarRemoto = async () => {
    setCargando(true);
    setError("");
    try {
      const res = await api.get("/portales/idealista-partners/remoto?state=active&size=20");
      setRemoto(res.data);
      setTab("remoto");
    } catch (e) {
      setRemoto(null);
      setError(e.response?.data?.error || e.message || "Error al listar remoto");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPreview(referenciaInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonText = tab === "preview"
    ? (preview ? JSON.stringify({ property: preview.json, images: preview.images }, null, 2) : "")
    : (remoto ? JSON.stringify(remoto.data, null, 2) : "");

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      await dialogs.alert("JSON copiado al portapapeles");
    } catch {
      await dialogs.alert("No se pudo copiar");
    }
  };

  const cob = preview?.cobertura;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="w-full max-w-4xl max-h-[90vh] rounded-xl flex flex-col overflow-hidden"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div>
            <h3 className="font-bold text-lg" style={{ color: t.textPrimary }}>Idealista Partners — JSON API</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              Sandbox. Marca la ficha en la matriz (como Fotocasa): se da de alta o de baja por API.
              Idealista copia producción cada noche; eso no lo vaciamos. Lo que tú actives es lo que gestiona el CRM.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm px-2 py-1 rounded" style={{ color: t.textMuted }}>✕</button>
        </div>

        <div className="px-5 py-3 flex flex-wrap items-end gap-2 shrink-0" style={{ borderBottom: `1px solid ${t.border}` }}>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Referencia</label>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") cargarPreview(ref); }}
              placeholder="3610-C"
              className="w-full px-2.5 py-1.5 rounded-lg text-sm"
              style={{ background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary }}
            />
          </div>
          <button type="button" onClick={() => cargarPreview(ref)} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: t.accentBlue, opacity: cargando ? 0.6 : 1 }}>
            Ver JSON envío
          </button>
          <button type="button" onClick={cargarRemoto} disabled={cargando}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ border: `1px solid ${t.border}`, color: t.textSecondary }}>
            Listado sandbox
          </button>
          {jsonText && (
            <button type="button" onClick={copiar}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ color: t.accentGreen }}>
              Copiar JSON
            </button>
          )}
        </div>

        <div className="px-5 pt-2 flex gap-2 shrink-0">
          <button type="button" onClick={() => setTab("preview")}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "preview" ? "#fff" : t.textSecondary,
              background: tab === "preview" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            JSON que enviaríamos
          </button>
          <button type="button" onClick={() => { setTab("remoto"); if (!remoto) cargarRemoto(); }}
            className="px-2.5 py-1 rounded text-xs font-semibold"
            style={{
              color: tab === "remoto" ? "#fff" : t.textSecondary,
              background: tab === "remoto" ? t.accentBlue : (isDark ? t.bgPanel : "#f1f5f9"),
            }}>
            En sandbox ahora{remoto ? ` (${remoto.totalProperties})` : ""}
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-2 text-xs px-3 py-2 rounded" style={{ background: isDark ? "#3f1d1d" : "#fef2f2", color: t.accentRed }}>
            {error}
          </div>
        )}

        {tab === "preview" && cob && (
          <div className="px-5 pt-2 text-[11px] space-y-1 shrink-0" style={{ color: t.textMuted }}>
            <div>
              {cob.listo
                ? "Campos obligatorios cubiertos."
                : `Faltan ${cob.faltan?.length || 0} obligatorios`}
            </div>
            {(cob.faltan || []).map((f) => (
              <div key={f.campo} style={{ color: t.accentRed }}>Falta {f.campo}: {f.motivo}</div>
            ))}
            {(cob.recomendados || []).map((f) => (
              <div key={f.campo}>Recomendado {f.campo}: {f.motivo}</div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-3 min-h-0">
          {cargando ? (
            <p className="text-sm" style={{ color: t.textMuted }}>Cargando…</p>
          ) : (
            <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-all rounded-lg p-3"
              style={{ background: isDark ? t.bgPanel : "#f8fafc", color: t.textPrimary, border: `1px solid ${t.border}` }}>
              {jsonText || "Sin datos"}
            </pre>
          )}
        </div>

        <div className="px-5 py-3 shrink-0" style={{ borderTop: `1px solid ${t.border}` }}>
          <button type="button" onClick={onClose} className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Sección 1: Configuración ───────────────────────────────────────────────

function SeccionConfig({ theme, onIrAMatriz }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const veNuevo = puede("cfg_portales_nuevo", "ver");
  const altaNuevo = puede("cfg_portales_nuevo", "alta");
  const veEditar = puede("cfg_portales_editar", "ver");
  const modEditar = puede("cfg_portales_editar", "modificar");
  const veEliminar = puede("cfg_portales_eliminar", "ver");
  const bajaEliminar = puede("cfg_portales_eliminar", "baja");
  const veFormato = puede("cfg_portales_formato", "ver");
  const veContador = puede("cfg_portales_contador", "ver");
  const altaPortal = puede("cfg_portales", "alta");
  const modPortal = puede("cfg_portales", "modificar");
  const bajaPortal = puede("cfg_portales", "baja");
  const [portales, setPortales] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [stats, setStats] = useState({});
  const [modalFeed, setModalFeed] = useState(null);
  const [modalFeedImport, setModalFeedImport] = useState(null);
  const [modalFeedProps, setModalFeedProps] = useState(null);
  const [modalPortal, setModalPortal] = useState(null);
  const [modalFotocasaJson, setModalFotocasaJson] = useState(false);
  const [modalPisosJson, setModalPisosJson] = useState(false);
  const [modalIdealistaJson, setModalIdealistaJson] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [sincronizandoImportId, setSincronizandoImportId] = useState(null);
  const [sincronizandoExportSoopremaId, setSincronizandoExportSoopremaId] = useState(null);

  const cargar = async () => {
    try {
      const [resPortales, resFeeds, resStats] = await Promise.allSettled([
        api.get("/portales"),
        api.get("/portales/feeds/all"),
        api.get("/portales/stats"),
      ]);
      if (resPortales.status === "fulfilled") setPortales(resPortales.value.data.data || []);
      if (resFeeds.status === "fulfilled") setFeeds(resFeeds.value.data.data || []);
      if (resStats.status === "fulfilled") {
        const statsMap = {};
        (resStats.value.data.data || []).forEach(s => { statsMap[s.id] = s; });
        setStats(statsMap);
      }
    } catch (err) {
      console.error("Error cargando:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleSaveFeed = async (data) => {
    const { _id, ...feedData } = data;
    if (_id) {
      await api.put(`/portales/feeds/${_id}`, feedData);
    } else {
      await api.post("/portales/feeds", feedData);
    }
    await cargar();
  };

  const handleDeleteFeed = async (id) => {
    if (!(await dialogs.confirm("¿Eliminar este feed?"))) return;
    await api.delete(`/portales/feeds/${id}`);
    await cargar();
  };

  const handleSincronizarExportSooprema = async (feed) => {
    const xmlUrl = `${apiBase}/api/portales/feeds/${feed.id}/xml`;
    const ce = parseFeedConfigExtra(feed);
    const tienePushApi = !!String(ce.sooprema_import_path || "").trim();

    if (!tienePushApi) {
      await dialogs.alert(
        [
          "VitalCasa genera el XML del feed; Sooprema lo descarga al sincronizar (no hay push API configurado).",
          "",
          "Para actualizar fotos y datos en Sooprema:",
          "1. En Sooprema: XML FEED → Importaciones → tu importación VitalCasa",
          "2. Pulsa «Sincronizar»",
          "",
          "URL del feed (cópiala si hace falta):",
          xmlUrl,
        ].join("\n")
      );
      return;
    }

    setSincronizandoExportSoopremaId(feed.id);
    try {
      const r = await api.post(`/portales/feeds/${feed.id}/sincronizar-export-sooprema`);
      const d = r.data?.data || {};
      await dialogs.alert(`Push Sooprema: ${d.pushed ?? 0} enviadas, ${d.failed ?? 0} fallidas (${d.feedTotal ?? d.total ?? 0} en el feed).`);
      await cargar();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "Error al sincronizar con Sooprema";
      await dialogs.alert(msg);
    } finally {
      setSincronizandoExportSoopremaId(null);
    }
  };

  const handleSincronizarImportacion = async (id) => {
    setSincronizandoImportId(id);
    try {
      const r = await api.post(`/portales/feeds/${id}/sincronizar-importacion`);
      const d = r.data?.data || {};
      await dialogs.alert(`Importación completada: ${d.created ?? 0} nuevas, ${d.updated ?? 0} actualizadas (${d.total ?? 0} en el XML).`);
      await cargar();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error al importar");
    } finally {
      setSincronizandoImportId(null);
    }
  };

  const handleSavePortal = async (data) => {
    const { _id, ...portalData } = data;
    if (_id) {
      await api.put(`/portales/${_id}`, portalData);
    } else {
      await api.post("/portales", portalData);
    }
    await cargar();
  };

  const [togglingActivoId, setTogglingActivoId] = useState(null);

  const handleTogglePortalActivo = async (portal) => {
    if (!portal?.id || togglingActivoId) return;
    const next = portal.activo === false;
    setTogglingActivoId(portal.id);
    // Optimistic UI
    setPortales((prev) =>
      prev.map((p) => (Number(p.id) === Number(portal.id) ? { ...p, activo: next } : p))
    );
    try {
      await api.put(`/portales/${portal.id}`, { activo: next });
      await cargar();
    } catch (err) {
      setPortales((prev) =>
        prev.map((p) => (Number(p.id) === Number(portal.id) ? { ...p, activo: portal.activo } : p))
      );
      await dialogs.alert(err.response?.data?.error || err.message || "No se pudo cambiar el estado");
    } finally {
      setTogglingActivoId(null);
    }
  };

  const handleDeletePortal = async (id) => {
    if (!(await dialogs.confirm("¿Eliminar este portal?"))) return;
    await api.delete(`/portales/${id}`);
    await cargar();
  };

  if (cargando) return <div className="text-center py-10" style={{ color: t.textMuted }}>Cargando...</div>;

  const feedsExport = feeds.filter(f => (f.modo || "export") !== "import");
  const feedsImport = feeds.filter(f => (f.modo || "export") === "import");
  const apiBase = window.location.hostname === "localhost" ? "http://localhost:4000" : "https://vitalcasa-crm-production.up.railway.app";

  return (
    <div className="space-y-6">
      {/* PORTALES — sección principal */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: t.textMuted }}>Portales</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              Solo los portales en ON salen como columna en «Propiedades × Portales». Ahí marcas o quitas cada inmueble.
            </p>
          </div>
          {altaPortal && (
          <button onClick={() => setModalPortal({})} className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue }}>
            + Nuevo Portal
          </button>
          )}
        </div>
        <div className="rounded-xl overflow-hidden overflow-x-auto" style={{ border: `1px solid ${t.border}` }}>
          <table className="w-full min-w-[920px]">
            <thead>
              <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                <th className="text-left px-2 py-2 text-[10px] font-bold uppercase whitespace-nowrap" style={{ color: t.textMuted, width: 130 }}>Portal</th>
                <th className="text-center px-1.5 py-2 text-[10px] font-bold uppercase" style={{ color: t.textMuted }}>Tipo</th>
                <th className="text-center px-1.5 py-2 text-[10px] font-bold uppercase" style={{ color: t.textMuted }}>Estado</th>
                <th className="text-left px-2 py-2 text-[12px] font-bold uppercase" style={{ color: t.textMuted }}>Nuestros anuncios</th>
                <th className="text-left px-2 py-2 text-[12px] font-bold uppercase" style={{ color: t.textMuted }}>API Key</th>
                <th className="text-left px-2 py-2 text-[12px] font-bold uppercase" style={{ color: t.textMuted }}>URL API</th>
                <th className="text-left px-2 py-2 text-[12px] font-bold uppercase" style={{ color: t.textMuted }}>Feed</th>
                <th className="text-right px-2 py-2 text-[10px] font-bold uppercase" style={{ color: t.textMuted }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {portales.map(portal => {
                const esApi = String(portal.tipo || "").toLowerCase() === "api";
                const hasKey = Boolean(String(portal.api_key || "").trim());
                const keyHint = hasKey
                  ? `···${String(portal.api_key).slice(-4)}`
                  : (esApi ? "env" : "—");
                const siteUrl = String(portal.url_anuncios || "").trim();
                const feedLinked = feedsExport.find((f) => Number(f.portal_id) === Number(portal.id));
                const feedFromPortal = String(portal.feed_url || "").trim();
                // API: feed_url = override de host API. Feed XML: feed_url o XML de exportación vinculado.
                const apiUrl = esApi ? feedFromPortal : "";
                const feedFileUrl = !esApi
                  ? (feedFromPortal || (feedLinked ? `${apiBase}/api/portales/feeds/${feedLinked.id}/xml` : ""))
                  : (feedLinked ? `${apiBase}/api/portales/feeds/${feedLinked.id}/xml` : "");
                const truncHost = (u, n = 34) => {
                  const s = String(u || "").replace(/^https?:\/\//i, "");
                  return s.length > n ? `${s.slice(0, n)}…` : s;
                };
                const apiUrlLabel = apiUrl
                  ? truncHost(apiUrl)
                  : (esApi ? "env Railway" : "—");
                const feedIsHttp = /^https?:\/\//i.test(feedFileUrl);
                const feedLabel = feedFileUrl
                  ? truncHost(feedFileUrl)
                  : "—";
                const linkGreen = t.accentGreen || "#16a34a";
                return (
                <tr key={portal.id} style={{ borderTop: `1px solid ${t.border}`, background: t.bgCard }}>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
                        style={{ background: portal.color || t.accentBlue }}>
                        {portal.nombre?.charAt(0)}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="text-xs font-semibold truncate max-w-[100px]" style={{ color: t.textPrimary }} title={portal.nombre}>{portal.nombre}</span>
                        {veContador && (
                        <PortalPropCountBadge
                          compact
                          count={stats[portal.id]?.total_props ?? 0}
                          contratados={stats[portal.id]?.anuncios_contratados ?? portal.anuncios_contratados}
                          portalColor={portal.color}
                          isDark={isDark}
                          accentFallback={t.accentBlue}
                        />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-1.5 py-2 text-center">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase whitespace-nowrap"
                      style={{ background: esApi ? (isDark ? "#172040" : "#eff6ff") : (isDark ? "#1a2e1a" : "#ecfdf5"),
                               color: esApi ? t.accentBlue : (t.accentGreen || "#16a34a") }}>
                      {esApi ? "API" : "Feed XML"}
                    </span>
                  </td>
                  <td className="px-1.5 py-2 text-center">
                    {modPortal ? (
                      <button
                        type="button"
                        disabled={Number(togglingActivoId) === Number(portal.id)}
                        onClick={() => handleTogglePortalActivo(portal)}
                        className="px-2.5 py-1 rounded-lg text-xs font-bold transition-colors"
                        style={{
                          color: portal.activo !== false ? "#fff" : t.textMuted,
                          background: portal.activo !== false
                            ? (portal.color || t.accentGreen || "#16a34a")
                            : (isDark ? t.bgPanel : "#f1f5f9"),
                          border: `1px solid ${portal.activo !== false ? (portal.color || t.accentGreen || "#16a34a") : t.border}`,
                          opacity: Number(togglingActivoId) === Number(portal.id) ? 0.5 : 1,
                          minWidth: 52,
                        }}
                        title={portal.activo !== false
                          ? "Desactivar: no aparecerá en Propiedades × Portales"
                          : "Activar: aparecerá en Propiedades × Portales"}
                      >
                        {portal.activo !== false ? "ON" : "OFF"}
                      </button>
                    ) : (
                      <span
                        className="inline-block px-2.5 py-1 rounded-lg text-xs font-bold"
                        style={{
                          color: portal.activo !== false ? "#fff" : t.textMuted,
                          background: portal.activo !== false
                            ? (portal.color || t.accentGreen || "#16a34a")
                            : (isDark ? t.bgPanel : "#f1f5f9"),
                          border: `1px solid ${portal.activo !== false ? (portal.color || t.accentGreen || "#16a34a") : t.border}`,
                          minWidth: 52,
                        }}
                      >
                        {portal.activo !== false ? "ON" : "OFF"}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 max-w-[180px]">
                    {siteUrl ? (
                      <a
                        href={siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] font-mono truncate block hover:underline"
                        style={{ color: t.accentGreen || "#16a34a" }}
                        title={siteUrl}
                      >
                        {siteUrl.replace(/^https?:\/\//i, "").slice(0, 42)}
                        {siteUrl.replace(/^https?:\/\//i, "").length > 42 ? "…" : ""}
                      </a>
                    ) : (
                      <span className="text-[12px]" style={{ color: t.textMuted }} title="Editar portal → URL de nuestros anuncios">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className="text-[12px] font-mono"
                      style={{ color: hasKey ? t.textSecondary : t.textMuted }}
                      title={hasKey ? "Api-Key en CRM (BD)" : (esApi ? "Sin key en BD → variables Railway" : "Sin API Key")}
                    >
                      {keyHint}
                    </span>
                  </td>
                  <td className="px-2 py-2 max-w-[140px]">
                    {apiUrl && /^https?:\/\//i.test(apiUrl) ? (
                      <a
                        href={apiUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] font-mono truncate block hover:underline"
                        style={{ color: linkGreen }}
                        title={apiUrl}
                      >
                        {apiUrlLabel}
                      </a>
                    ) : (
                      <span
                        className="text-[12px] font-mono truncate block"
                        style={{ color: t.textMuted }}
                        title={esApi ? "Host de la API (variables Railway si vacío)" : "Solo aplica a portales API"}
                      >
                        {apiUrlLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 max-w-[140px]">
                    {feedIsHttp ? (
                      <a
                        href={feedFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] font-mono truncate block hover:underline"
                        style={{ color: linkGreen }}
                        title={feedFileUrl}
                      >
                        {feedLabel}
                      </a>
                    ) : (
                      <span className="text-[12px] font-mono truncate block" style={{ color: t.textMuted }} title={feedFileUrl || "Sin fichero XML"}>
                        {feedLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex justify-end items-center gap-1">
                      <button onClick={() => onIrAMatriz(portal.id)} className="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1"
                        style={{ color: t.accentGreen }} title="Ver/asignar propiedades">
                        Propiedades
                      </button>
                      {String(portal.slug || "").toLowerCase() === "fotocasa" && (
                        <button
                          type="button"
                          onClick={() => setModalFotocasaJson(true)}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ color: t.accentBlue }}
                          title="Ver JSON enviado a Fotocasa Pro API"
                        >
                          Ver JSON API
                        </button>
                      )}
                      {String(portal.slug || "").toLowerCase() === "pisos" && (
                        <button
                          type="button"
                          onClick={() => setModalPisosJson(true)}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ color: t.accentBlue }}
                          title="Ver JSON enviado a Habitatsoft / Pisos.com"
                        >
                          Ver JSON API
                        </button>
                      )}
                      {String(portal.slug || "").toLowerCase() === "idealista" && (
                        <button
                          type="button"
                          onClick={() => setModalIdealistaJson(true)}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ color: t.accentBlue }}
                          title="Ver JSON Idealista Partners (sandbox)"
                        >
                          Ver JSON API
                        </button>
                      )}
                      {modPortal && (
                      <button onClick={() => setModalPortal(portal)} className="px-2 py-1 rounded text-xs font-medium"
                        style={{ color: t.accentBlue }}>Editar</button>
                      )}
                      {bajaPortal && (
                      <button onClick={() => handleDeletePortal(portal.id)} className="px-2 py-1 rounded text-xs font-medium"
                        style={{ color: t.accentRed }}>Eliminar</button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
              {portales.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm" style={{ color: t.textMuted }}>
                    No hay portales. Pulsa "+ Nuevo Portal" para crear uno.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalFotocasaJson && (
        <ModalFotocasaProJson theme={theme} onClose={() => setModalFotocasaJson(false)} />
      )}
      {modalPisosJson && (
        <ModalPisosJson theme={theme} onClose={() => setModalPisosJson(false)} />
      )}
      {modalIdealistaJson && (
        <ModalIdealistaPartnersJson theme={theme} onClose={() => setModalIdealistaJson(false)} />
      )}

      {/* FEEDS EXPORTACIÓN */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: t.textMuted }}>Exportación — feeds XML</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              VitalCasa genera un XML con las propiedades que marques; comparte la URL con portales y colaboradores
            </p>
          </div>
          {veNuevo && altaNuevo && (
          <button type="button" onClick={() => setModalFeed({})} className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}>
            + Nuevo feed exportación
          </button>
          )}
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Nombre</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>URL</th>
                {veFormato && (
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Formato</th>
                )}
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Portal</th>
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Estado</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {feedsExport.map(feed => {
                const xmlUrl = `${apiBase}/api/portales/feeds/${feed.id}/xml`;
                const formatoLabel = FORMATOS_XML.find(f => f.value === feed.formato_xml)?.label || "Kyero v3";
                const feedCfg = parseFeedConfigExtra(feed);
                const privados = feedCfg.incluir_datos_privados === true;
                const propsEnFeed = feed.portal_id && !feed.props_activas_feed
                  ? (stats[feed.portal_id]?.total_props ?? feed.total_props ?? 0)
                  : (feed.props_activas_feed ?? 0);
                return (
                  <tr key={feed.id} style={{ borderTop: `1px solid ${t.border}`, background: t.bgCard }}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold" style={{ color: t.textPrimary }}>
                        {feed.nombre || feed.portal_nombre || "Feed #" + feed.id}
                      </span>
                      {veContador && propsEnFeed > 0 && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: isDark ? t.bgPanel : "#e2e8f0", color: t.textMuted }}>
                          {propsEnFeed} en XML
                        </span>
                      )}
                      {privados && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                          style={{ background: isDark ? "#422006" : "#fef3c7", color: isDark ? "#fbbf24" : "#b45309" }}
                          title="Este feed incluye dirección, precio propietario y datos del propietario">
                          Privados
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 max-w-md">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold" style={{ color: t.textMuted }}>XML</span>
                          <span className="text-xs font-mono truncate block" style={{ color: t.textMuted }}>{xmlUrl}</span>
                          <CopyUrlButton text={xmlUrl} theme={theme} title="Copiar URL del XML generado" />
                        </div>
                        {feed.url_feed && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase font-bold" style={{ color: t.textMuted }}>Web</span>
                            <span className="text-xs font-mono truncate" style={{ color: t.textMuted }}>{feed.url_feed}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    {veFormato && (
                    <td className="px-4 py-3 text-center">
                      <span className="text-[10px] px-2 py-1 rounded-full font-bold uppercase"
                        style={{ background: isDark ? t.bgPanel : "#e2e8f0", color: t.textMuted }}>
                        {formatoLabel}
                      </span>
                    </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      {feed.portal_nombre ? (
                        <div className="inline-flex flex-col items-center gap-1.5">
                          <div className="inline-flex items-center gap-1.5 justify-center flex-wrap">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                              style={{ background: feed.portal_color || t.accentBlue }}>
                              {feed.portal_nombre.charAt(0)}
                            </div>
                            <span className="text-xs font-medium" style={{ color: t.textSecondary }}>{feed.portal_nombre}</span>
                            {veContador && (
                            <PortalPropCountBadge
                              compact
                              count={stats[feed.portal_id]?.total_props ?? 0}
                              contratados={stats[feed.portal_id]?.anuncios_contratados}
                              portalColor={feed.portal_color}
                              isDark={isDark}
                              accentFallback={t.accentBlue}
                            />
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs" style={{ color: t.textMuted }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: feed.activo ? t.accentGreen : t.accentRed }} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end items-center gap-1 flex-wrap">
                        <button type="button" onClick={() => setModalFeedProps(feed)} className="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1"
                          style={{ color: t.accentGreen }} title="Elegir propiedades para este feed">
                          Propiedades
                          {veContador && propsEnFeed > 0 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "#10b98120", color: t.accentGreen }}>
                              {propsEnFeed}
                            </span>
                          )}
                        </button>
                        {feed.portal_id && (
                          <button type="button" onClick={() => onIrAMatriz(feed.portal_id)} className="px-2 py-1 rounded text-xs font-medium"
                            style={{ color: t.textMuted }} title="Matriz global del portal asociado">
                            Portal
                          </button>
                        )}
                        <a href={xmlUrl} target="_blank" rel="noopener noreferrer"
                          className="px-2 py-1 rounded text-xs font-medium" style={{ color: "#8b5cf6" }}>Ver XML</a>
                        {feed.formato_xml === "sooprema" && (() => {
                          const pushApi = !!String(parseFeedConfigExtra(feed).sooprema_import_path || "").trim();
                          return (
                            <button type="button" disabled={!feed.activo || sincronizandoExportSoopremaId === feed.id}
                              onClick={() => handleSincronizarExportSooprema(feed)}
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ color: feed.activo && sincronizandoExportSoopremaId !== feed.id ? "#0d9488" : t.textMuted }}
                              title={pushApi
                                ? "Enviar propiedades a Sooprema vía API REST"
                                : "Cómo sincronizar el feed en Sooprema (Importaciones → Sincronizar)"}>
                              {sincronizandoExportSoopremaId === feed.id
                                ? "Enviando…"
                                : (pushApi ? "Push API" : "Sync Web")}
                            </button>
                          );
                        })()}
                        {veEditar && (
                        <button type="button" onClick={() => setModalFeed(feed)} disabled={!modEditar} title={!modEditar ? "No tienes permiso para editar feeds" : undefined}
                          className="px-2 py-1 rounded text-xs font-medium" style={{ color: t.accentBlue, opacity: modEditar ? 1 : 0.5, cursor: modEditar ? "pointer" : "not-allowed" }}>Editar</button>
                        )}
                        {veEliminar && (
                        <button type="button" onClick={() => handleDeleteFeed(feed.id)} disabled={!bajaEliminar} title={!bajaEliminar ? "No tienes permiso para eliminar feeds" : undefined}
                          className="px-2 py-1 rounded text-xs font-medium" style={{ color: t.accentRed, opacity: bajaEliminar ? 1 : 0.5, cursor: bajaEliminar ? "pointer" : "not-allowed" }}>Eliminar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {feedsExport.length === 0 && (
                <tr>
                  <td colSpan={5 + (veFormato ? 1 : 0)} className="px-4 py-8 text-center text-sm" style={{ color: t.textMuted }}>
                    No hay feeds de exportación. Pulsa «+ Nuevo feed exportación».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* FEEDS IMPORTACIÓN */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: t.textMuted }}>Importación — CRM antiguo</h3>
            <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>
              Descarga el XML desde una URL externa, elige el mismo formato que en exportación y sincroniza propiedades por referencia
            </p>
          </div>
          {veNuevo && altaNuevo && (
          <button type="button" onClick={() => setModalFeedImport({})} className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: t.accentBlue }}>
            + Nueva importación
          </button>
          )}
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: isDark ? t.bgPanel : "#f1f5f9" }}>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Nombre</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>URL origen</th>
                {veFormato && (
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Formato</th>
                )}
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Última sync</th>
                <th className="text-center px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Estado</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold uppercase" style={{ color: t.textMuted }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {feedsImport.map(feed => {
                const formatoLabel = FORMATOS_XML.find(f => f.value === feed.formato_xml)?.label || "Kyero v3";
                const ultima = feed.ultima_sync
                  ? new Date(feed.ultima_sync).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
                  : "—";
                return (
                  <tr key={feed.id} style={{ borderTop: `1px solid ${t.border}`, background: t.bgCard }}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold" style={{ color: t.textPrimary }}>{feed.nombre || "Importación #" + feed.id}</span>
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{ background: isDark ? "#1e3a2f" : "#d1fae5", color: t.accentGreen }}>
                        Importación
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 max-w-md">
                        <span className="text-xs font-mono truncate" style={{ color: t.textMuted }} title={feed.url_feed || ""}>
                          {feed.url_feed || "—"}
                        </span>
                        {feed.url_feed && (
                          <CopyUrlButton text={feed.url_feed} theme={theme} title="Copiar URL del feed" />
                        )}
                      </div>
                    </td>
                    {veFormato && (
                    <td className="px-4 py-3 text-center">
                      <span className="text-[10px] px-2 py-1 rounded-full font-bold uppercase"
                        style={{ background: isDark ? t.bgPanel : "#e2e8f0", color: t.textMuted }}>{formatoLabel}</span>
                    </td>
                    )}
                    <td className="px-4 py-3 text-center text-xs" style={{ color: t.textSecondary }}>
                      <div>{ultima}</div>
                      {feed.total_props > 0 && (
                        <div className="text-[10px] mt-0.5" style={{ color: t.textMuted }}>{feed.total_props} en último XML</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: feed.activo ? t.accentGreen : t.accentRed }} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end items-center gap-1 flex-wrap">
                        <button type="button" onClick={() => setModalFeedProps(feed)} className="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1"
                          style={{ color: t.accentGreen }} title="Ver propiedades traídas o vinculadas a esta importación">
                          Propiedades
                          {veContador && (feed.props_activas_feed > 0) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "#10b98120", color: t.accentGreen }}>
                              {feed.props_activas_feed}
                            </span>
                          )}
                        </button>
                        <button type="button" disabled={!feed.activo || sincronizandoImportId === feed.id}
                          onClick={() => handleSincronizarImportacion(feed.id)}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            color: "#fff",
                            background: feed.activo && sincronizandoImportId !== feed.id ? t.accentGreen : t.textMuted,
                            opacity: feed.activo ? 1 : 0.5,
                          }}>
                          {sincronizandoImportId === feed.id ? "Sincronizando…" : "Sincronizar"}
                        </button>
                        {veEditar && (
                        <button type="button" onClick={() => setModalFeedImport(feed)} disabled={!modEditar} title={!modEditar ? "No tienes permiso para editar feeds" : undefined}
                          className="px-2 py-1 rounded text-xs font-medium" style={{ color: t.accentBlue, opacity: modEditar ? 1 : 0.5, cursor: modEditar ? "pointer" : "not-allowed" }}>Editar</button>
                        )}
                        {veEliminar && (
                        <button type="button" onClick={() => handleDeleteFeed(feed.id)} disabled={!bajaEliminar} title={!bajaEliminar ? "No tienes permiso para eliminar feeds" : undefined}
                          className="px-2 py-1 rounded text-xs font-medium" style={{ color: t.accentRed, opacity: bajaEliminar ? 1 : 0.5, cursor: bajaEliminar ? "pointer" : "not-allowed" }}>Eliminar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {feedsImport.length === 0 && (
                <tr>
                  <td colSpan={5 + (veFormato ? 1 : 0)} className="px-4 py-8 text-center text-sm" style={{ color: t.textMuted }}>
                    No hay importaciones. Pulsa «+ Nueva importación» y pega la URL del XML de tu CRM anterior.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalFeed !== null && (
        <ModalFeed
          feed={modalFeed.id ? modalFeed : null}
          portales={portales}
          portalesStats={stats}
          onClose={() => setModalFeed(null)}
          onSave={handleSaveFeed}
          theme={theme}
          puede={puede}
        />
      )}

      {modalFeedImport !== null && (
        <ModalFeedImport
          feed={modalFeedImport.id ? modalFeedImport : null}
          onClose={() => setModalFeedImport(null)}
          onSave={handleSaveFeed}
          theme={theme}
          puede={puede}
        />
      )}

      {modalFeedProps !== null && (
        <ModalFeedPropiedades
          feed={modalFeedProps}
          theme={theme}
          onClose={() => setModalFeedProps(null)}
          onUpdated={cargar}
        />
      )}

      {modalPortal !== null && (
        <ModalPortal
          portal={modalPortal.id ? modalPortal : null}
          onClose={() => setModalPortal(null)}
          onSave={handleSavePortal}
          theme={theme}
        />
      )}
    </div>
  );
}

// ── Sección 2: Matriz Propiedades x Portales ──────────────────────────────

const MATRIZ_LIMITES = [10, 20, 50, 100, 150, 300, 500];
const MATRIZ_LS_KEY = "vc_portales_matriz_ui_v1";

const MATRIZ_FILTROS_DEFAULT = {
  referencia: "", tipo: "", poblacion: "", operacion: "",
  precio_min: "", precio_max: "", habitaciones_min: "", exclusiva: "todas",
  solo_activas: true, portal_ids: [], estado_portal: "todos", limit: 50,
};

function loadMatrizUiState() {
  try {
    const raw = sessionStorage.getItem(MATRIZ_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function saveMatrizUiState(state) {
  try {
    sessionStorage.setItem(MATRIZ_LS_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

function slugDeFalloSync(f) {
  return String(f?.portal_slug || "").trim().toLowerCase();
}

function nombreSugiereSlug(nombre, slug) {
  const n = String(nombre || "").toLowerCase();
  if (slug === "pisos") return /pisos|habitatsoft/.test(n);
  if (slug === "fotocasa") return /fotocasa/.test(n);
  if (slug === "idealista") return /idealista/.test(n);
  if (slug === "indomio") return /indomio/.test(n);
  if (slug === "habitaclia") return /habitaclia/.test(n);
  return n.includes(slug);
}

function falloEsDePortales(f, slugs) {
  if (!slugs.length) return true;
  const fs = slugDeFalloSync(f);
  if (fs && slugs.includes(fs)) return true;
  return slugs.some((s) => nombreSugiereSlug(f?.portal, s));
}

function SeccionMatriz({ theme, filtroPortalInicial, openFallosInicial = false, refreshEpoch = 0 }) {
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const navigate = useNavigate();
  const savedUi = useMemo(() => loadMatrizUiState(), []);

  const [propiedades, setPropiedades] = useState([]);
  const [portales, setPortales] = useState([]);
  const [matriz, setMatriz] = useState({});
  const [filtros, setFiltros] = useState(() => ({
    ...MATRIZ_FILTROS_DEFAULT,
    ...(savedUi?.filtros || {}),
    portal_ids: Array.isArray(savedUi?.filtros?.portal_ids) ? savedUi.filtros.portal_ids : [],
  }));
  const [opcionesFiltros, setOpcionesFiltros] = useState({ tipos: [], poblaciones: [], operaciones: [] });
  const [page, setPage] = useState(() => {
    const p = Number(savedUi?.page);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [toggling, setToggling] = useState(null);
  /** Job de sync en servidor (Fotocasa/Pisos/Idealista). Sobrevive cambiar de página. */
  const [bulkSync, setBulkSync] = useState(null);
  const [bulkElapsedSec, setBulkElapsedSec] = useState(0);
  const bulkEnqueueRef = useRef(false);
  const bulkFinishedNotifiedRef = useRef(null);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(
    savedUi?.filtrosAbiertos != null ? !!savedUi.filtrosAbiertos : true
  );
  /** Misma vista previa / visor que Promociones. */
  const [ficha, setFicha] = useState({ abierta: false, pack: null, cargando: false });
  const [visor, setVisor] = useState({ abierto: false, fotos: [], indice: 0 });
  /** Orden servidor: referencia | fecha_alta | precio. Sin flechas; clic alterna asc/desc. */
  const [sortCol, setSortCol] = useState(() => savedUi?.sortCol || null);
  const [sortDir, setSortDir] = useState(() => savedUi?.sortDir || "desc");
  const debounceRef = useRef(null);
  const skipResetPageRef = useRef(true);
  /** Igual que Propiedades: ignora respuestas obsoletas al cambiar/limpiar referencia. */
  const matrizRequestIdRef = useRef(0);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [highlightPortal, setHighlightPortal] = useState(
    filtroPortalInicial != null ? filtroPortalInicial : (savedUi?.highlightPortal ?? null)
  );
  const [statsPortales, setStatsPortales] = useState({});
  const [contadoresPortales, setContadoresPortales] = useState({});
  /** Fallos de sync Portales→API (servidor + caché local). */
  const [syncFallos, setSyncFallos] = useState([]);
  const [syncFallosAbiertos, setSyncFallosAbiertos] = useState(!!openFallosInicial);
  const [syncFallosCargando, setSyncFallosCargando] = useState(false);
  const fallosRestoreDoneRef = useRef(false);

  useEffect(() => {
    saveMatrizUiState({
      filtros,
      page,
      sortCol,
      sortDir,
      highlightPortal,
      filtrosAbiertos,
    });
  }, [filtros, page, sortCol, sortDir, highlightPortal, filtrosAbiertos]);

  useEffect(() => {
    if (filtroPortalInicial != null) setHighlightPortal(filtroPortalInicial);
  }, [filtroPortalInicial]);

  const humanizarErrorIdealista = (text) => {
    const t = String(text || "");
    if (!t.trim()) return null;
    // 500 Idealista: no es ficha (evitar doble envoltorio «Idealista no ha aceptado (Idealista…)»).
    if (/Internal Server Error|HTTP\s*500|error interno \(HTTP 500\)|sandbox falló con un error interno/i.test(t)) {
      return "Idealista sandbox falló con un error interno (HTTP 500). No es un dato de la ficha; reintenta en un momento o vuelve a marcar Idealista.";
    }
    // Ya claro para secretaria (backend nuevo) — no re-envolver ni recortar listados
    if (/^(Falta |Faltan |No se puede publicar|Revisa |Idealista no admite|Idealista necesita|Idealista no ha aceptado|Idealista sandbox|Idealista no respondió|Los m²|La planta|La puerta|El estado)/i.test(t)
        && !/features\.|DATAFEED|countrynonbuildable/i.test(t)) {
      return t;
    }
    if (/nosotros |→ Idealista/i.test(t) && !/features\.|DATAFEED|Motivo API/i.test(t)) return t;
    const tips = [];
    if (/land classification|Clasificación de suelo|parcela urbana|solar\/parcela/i.test(t)) {
      tips.push("No se puede publicar este solar/parcela urbana en Idealista de momento. Mientras, no marques Idealista en esta ficha.");
    }
    if (/new development conservation|conservation value|Conservación:/i.test(t)) {
      tips.push("El estado de conservación no vale para Idealista. Pon «Buen estado», «Reformado» o «Para reformar» y vuelve a marcar Idealista.");
    }
    if (/not allowed for studio|rooms number not allowed for studio|studio.*rooms|rooms.*studio/i.test(t)) {
      tips.push("Esta ficha es un estudio. Idealista no admite dormitorios en estudios. Si tiene habitaciones, cambia el tipo a Piso; si es estudio, déjalo así y vuelve a marcar Idealista.");
    } else if (/room number not valid|Habitaciones:|debe.*dormitorio|necesita al menos 1 dormitorio/i.test(t)) {
      tips.push("Idealista necesita al menos 1 dormitorio. Ponlo en la ficha y vuelve a marcar Idealista.");
    }
    // Mensaje ya con listado completo («Faltan N datos: 1) …; 2) …») — no recortar a un solo tip.
    if (/Faltan \d+ datos en la ficha:/i.test(t) || /Falta .+ en la ficha\. Corrígelo/i.test(t)) {
      return t;
    }
    // Solo si el motivo es CP/población/título de verdad — no si el texto genérico menciona «población, CP…».
    if (/address\.postalCode/i.test(t) || /Falta un c[oó]digo postal|c[oó]digo postal v[aá]lido de 5|CP 5 d[ií]gitos/i.test(t)) {
      tips.push("Falta un código postal válido de 5 números en la ficha. Corrígelo y vuelve a marcar Idealista.");
    }
    if (/address\.town/i.test(t) || /Falta la poblaci[oó]n en la ficha/i.test(t)) {
      tips.push("Falta la población en la ficha. Elígela y vuelve a marcar Idealista.");
    }
    if (/web_titulo|t[ií]tulo web/i.test(t)) {
      tips.push("Falta el título web en la ficha. Ponlo en Publicación web y vuelve a marcar Idealista.");
    }
    if (/address floor|Planta:/i.test(t)) {
      tips.push("La planta no es válida. Usa «Planta baja» o un número 1–59 y vuelve a marcar Idealista.");
    }
    if (/usable area|m²:|útiles/i.test(t)) {
      tips.push("Los m² útiles deben ser menores que los construidos. Corrígelo y vuelve a marcar Idealista.");
    }
    if (/must have a property whose name is type|house type/i.test(t)) {
      tips.push("Revisa el tipo de inmueble en la ficha y vuelve a marcar Idealista.");
    }
    // Desconocido: conservar el motivo real (no sustituir por un tip genérico de CP).
    if (!tips.length && t.trim()) {
      if (/^(Falta |No se puede|Revisa |Idealista |Los m²|La planta|La puerta|El estado)/i.test(t)) {
        return t;
      }
      const rawCorto = t.length > 280 ? `${t.slice(0, 277)}…` : t;
      tips.push(`Idealista no ha aceptado el anuncio (${rawCorto}).`);
    }
    return tips.length ? [...new Set(tips)].join(" ") : null;
  };

  const textoErrorSync = (v) => {
    if (v == null || v === "") return "Error desconocido";
    let s;
    if (typeof v === "string") s = v;
    else if (typeof v === "number" || typeof v === "boolean") s = String(v);
    else if (typeof v === "object") {
      if (typeof v.message === "string" && v.message) s = v.message;
      else if (typeof v.error === "string" && v.error) s = v.error;
      else if (typeof v.Message === "string" && v.Message) s = v.Message;
      else {
        try {
          s = JSON.stringify(v);
        } catch {
          s = "Error desconocido";
        }
      }
    } else s = String(v);
    // Nunca mostrar jerga axios a la secretaria
    if (/Request failed with status code 502/i.test(s) || (/\b502\b/.test(s) && /failed|gateway/i.test(s))) {
      return "Idealista no respondió a tiempo. Espera un momento y vuelve a intentarlo; el detalle está en «Fallos de sincronización».";
    }
    if (/Request failed with status code 504/i.test(s) || /Request failed with status code 503/i.test(s)) {
      return "Idealista está tardando demasiado. Espera un momento y vuelve a intentarlo.";
    }
    if (/Request failed with status code (\d+)/i.test(s)) {
      return "No se pudo sincronizar con el portal. Revisa «Fallos de sincronización» abajo.";
    }
    return s;
  };

  const syncAlertLockRef = useRef({ at: 0, msg: "" });
  const alertSyncFalloUnaVez = async (msg) => {
    const now = Date.now();
    const text = String(msg || "");
    // Evitar 10 popups iguales al marcar en ráfaga
    if (syncAlertLockRef.current.msg === text && now - syncAlertLockRef.current.at < 8000) {
      return;
    }
    syncAlertLockRef.current = { at: now, msg: text };
    await dialogs.alert(`${text}\n\nEl detalle queda en el panel «Fallos de sincronización» abajo.`);
  };

  const normalizarFilaFallo = (e, portalNombre = "Portal", now = new Date().toISOString()) => {
    const slug = String(e.portal_slug || "").toLowerCase();
    let error = textoErrorSync(e.error || e.message);
    if (slug === "idealista" || /idealista/i.test(String(e.portal || e.portal_nombre || portalNombre))) {
      error = humanizarErrorIdealista(error) || error;
    }
    return {
      id: String(e.id || `${now}-${e.propiedad_id || e.referencia || Math.random()}`),
      at: e.at || e.updated_at || e.created_at || now,
      portal: e.portal || e.portal_nombre || portalNombre,
      portal_slug: e.portal_slug || null,
      referencia: e.referencia || "—",
      propiedad_id: e.propiedad_id || null,
      error,
      code: e.code || (e.error && e.error.code) || null,
    };
  };

  const abrirFalloEnPropiedad = async (f) => {
    if (!f?.propiedad_id) {
      await dialogs.alert("Este fallo no tiene propiedad asociada.");
      return;
    }
    let portalId = null;
    const slug = String(f.portal_slug || "").toLowerCase();
    if (slug) {
      const fromStats = Object.values(statsPortales).find((p) => String(p.slug || "").toLowerCase() === slug);
      portalId = fromStats?.id || null;
      if (!portalId) {
        try {
          const res = await api.get("/portales");
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          portalId = list.find((p) => String(p.slug || "").toLowerCase() === slug)?.id || null;
        } catch {
          /* portal_id opcional: se resuelve al guardar */
        }
      }
    }
    navigate(`/propiedades/${f.propiedad_id}/editar`, {
      state: {
        returnTo: "/configuracion/portales",
        returnToState: { tab: "matriz", openFallos: true },
        portalSyncFix: {
          propiedadId: f.propiedad_id,
          portalId,
          portalSlug: slug || null,
          portalNombre: f.portal || "Portal",
          code: f.code || null,
          error: f.error || null,
        },
      },
    });
  };

  const cargarSyncFallos = useCallback(async () => {
    try {
      const res = await api.get("/portales/sync-fallos");
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setSyncFallos(rows.map((r) => normalizarFilaFallo(r)));
      return rows;
    } catch (err) {
      console.error("Error cargando sync-fallos:", err);
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await cargarSyncFallos();
      if (cancelled) return;
      // Si el panel está vacío, auditar sola una vez para rellenar bloqueos (sin m², etc.)
      if (!rows.length) {
        setSyncFallosCargando(true);
        try {
          const res = await api.post(
            "/portales/sync-fallos/auditar-fotocasa",
            {},
            { timeout: 120000 }
          );
          if (!cancelled) {
            const next = Array.isArray(res.data?.data) ? res.data.data : [];
            setSyncFallos(next.map((r) => normalizarFilaFallo(r)));
            // Al entrar en la página el panel queda recogido; no abrir por la auto-auditoría.
          }
        } catch (err) {
          console.error("Auto-auditoría sync-fallos:", err);
        } finally {
          if (!cancelled) setSyncFallosCargando(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cargarSyncFallos]);

  // Volver desde «Actualizar» ficha (portalSyncFix): matriz + Fallos desplegados y a la vista.
  useEffect(() => {
    if (!openFallosInicial || fallosRestoreDoneRef.current) return;
    fallosRestoreDoneRef.current = true;
    setSyncFallosAbiertos(true);
    cargarSyncFallos().finally(() => {
      const scroll = () => {
        const el = document.getElementById("portales-panel-fallos");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      window.setTimeout(scroll, 200);
      window.setTimeout(scroll, 600);
    });
  }, [openFallosInicial, cargarSyncFallos]);

  const aplicarSyncFallosRespuesta = (data) => {
    if (!data || !Array.isArray(data.sync_fallos)) return false;
    setSyncFallos(data.sync_fallos.map((r) => normalizarFilaFallo(r)));
    return true;
  };

  const registrarSyncFallos = (entradas, portalNombre = "Portal") => {
    const now = new Date().toISOString();
    const rows = (entradas || [])
      .filter((e) => e && (e.ok === false || e.error || e.warning || e.docsFailed))
      // Ok_205/206 son avisos de docs, no bloqueos de sync persistentes
      .filter((e) => {
        const code = String(e.code || e.error?.code || "");
        return code !== "Ok_205" && code !== "Ok_206" && !e.docsFailed;
      })
      .map((e) => normalizarFilaFallo(e, portalNombre, now));
    if (!rows.length) return;
    setSyncFallos((prev) => {
      const keys = new Set(rows.map((r) => `${r.propiedad_id || ""}|${r.referencia}|${r.portal}`));
      const rest = prev.filter((p) => !keys.has(`${p.propiedad_id || ""}|${p.referencia}|${p.portal}`));
      return [...rows, ...rest].slice(0, 300);
    });
    setSyncFallosAbiertos(true);
    // Refresco desde BD (el backend ya persistió el fallo en el sync)
    setTimeout(() => cargarSyncFallos(), 400);
  };

  const formatFechaAlta = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const toggleSortMatriz = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir(col === "fecha_alta" ? "desc" : "asc");
    }
  };

  const auditarSyncFallos = async () => {
    setSyncFallosCargando(true);
    try {
      const res = await api.post(
        "/portales/sync-fallos/auditar-fotocasa",
        {},
        { timeout: 120000 }
      );
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setSyncFallos(rows.map((r) => normalizarFilaFallo(r)));
      setSyncFallosAbiertos(true);
      const n = rows.length;
      const audit = res.data?.audit || {};
      await dialogs.alert(
        n
          ? `Auditoría Fotocasa: ${n} fallo(s) persistidos.\n` +
            `· Activas CRM: ${audit.activos_crm ?? "—"}\n` +
            `· Remoto API: ${audit.remoto_total ?? "—"} (ok=${audit.remoto_ok ? "sí" : "no"})\n` +
            `· Bloqueos mapper (sin ✓): ${audit.bloqueos_mapper_inactivas ?? "—"}`
          : "Auditoría Fotocasa: sin fallos detectados (CRM activo↔API y bloqueos mapper)."
      );
    } catch (err) {
      await dialogs.alert(textoErrorSync(err.response?.data?.error || err.response?.data || err.message) || "Error al auditar");
    } finally {
      setSyncFallosCargando(false);
    }
  };

  const limpiarSyncFallos = async () => {
    try {
      const qs = slugsFallosFiltro.length
        ? `?slugs=${encodeURIComponent(slugsFallosFiltro.join(","))}`
        : "";
      await api.delete(`/portales/sync-fallos${qs}`);
      if (slugsFallosFiltro.length) {
        setSyncFallos((prev) => prev.filter((f) => !falloEsDePortales(f, slugsFallosFiltro)));
      } else {
        setSyncFallos([]);
      }
    } catch (err) {
      await dialogs.alert(err.response?.data?.error || err.message || "Error al limpiar");
    }
  };

  /** Solo portales de la matriz (activo=true en Configuración). */
  const portalesFiltroOpciones = useMemo(() => {
    const base = (portales || []).filter((p) => p && p.activo !== false);
    return [...base].sort((a, b) =>
      String(a.nombre || "").localeCompare(String(b.nombre || ""), "es")
    );
  }, [portales]);

  /** Columnas visibles según chips de filtro (desmarcar = ocultar columna). */
  const portalesVisibles = useMemo(() => {
    const selected = Array.isArray(filtros.portal_ids) ? filtros.portal_ids : [];
    if (!selected.length) return [];
    const sel = new Set(selected.map((id) => Number(id)));
    return (portales || []).filter((p) => sel.has(Number(p.id)));
  }, [portales, filtros.portal_ids]);

  // Primera carga: todos seleccionados. Luego respetar clics del usuario.
  // Solo auto-añadir portales que acaban de pasar a ON en Configuración.
  const portalesMatrizIdsRef = useRef([]);
  const filtrosPortalInitRef = useRef(false);
  useEffect(() => {
    const ids = portalesFiltroOpciones.map((p) => p.id);
    if (!ids.length) return;

    const prevMatrix = portalesMatrizIdsRef.current;
    const newlyOn = ids.filter(
      (id) => !prevMatrix.some((x) => Number(x) === Number(id))
    );
    portalesMatrizIdsRef.current = ids;

    setFiltros((f) => {
      const prev = Array.isArray(f.portal_ids) ? f.portal_ids : [];
      // Quitar los que ya no están en la matriz (OFF en Configuración)
      let next = prev.filter((id) => ids.some((x) => Number(x) === Number(id)));

      if (!filtrosPortalInitRef.current) {
        filtrosPortalInitRef.current = true;
        next = ids;
      } else if (newlyOn.length) {
        for (const id of newlyOn) {
          if (!next.some((x) => Number(x) === Number(id))) next = [...next, id];
        }
      }

      const same =
        next.length === prev.length
        && next.every((id) => prev.some((x) => Number(x) === Number(id)));
      if (same) return f;
      return { ...f, portal_ids: next };
    });
  }, [portalesFiltroOpciones]);

  const slugsFallosFiltro = useMemo(() => {
    const fromList = (id) => {
      const p =
        portalesFiltroOpciones.find((x) => Number(x.id) === Number(id))
        || portales.find((x) => Number(x.id) === Number(id));
      return String(p?.slug || "").toLowerCase();
    };
    const ids = Array.isArray(filtros.portal_ids) ? filtros.portal_ids : [];
    if (ids.length) return ids.map(fromList).filter(Boolean);
    if (highlightPortal) {
      const s = fromList(highlightPortal);
      return s ? [s] : [];
    }
    return [];
  }, [filtros.portal_ids, highlightPortal, portalesFiltroOpciones, portales]);

  const syncFallosVisibles = useMemo(
    () => syncFallos.filter((f) => falloEsDePortales(f, slugsFallosFiltro)),
    [syncFallos, slugsFallosFiltro]
  );

  const tituloContador = (n, contratados) => {
    const num = Number(n) || 0;
    const cMax = contratados != null && contratados !== "" ? Number(contratados) : null;
    const cupo =
      Number.isFinite(cMax) && cMax >= 0
        ? ` · ${cMax.toLocaleString("es-ES")} contratados`
        : "";
    if (filtros.estado_portal === "inactivas") {
      return `${num.toLocaleString("es-ES")} propiedad${num === 1 ? "" : "es"} inactiva${num === 1 ? "" : "s"} (según filtros)${cupo}`;
    }
    return `${num.toLocaleString("es-ES")} propiedad${num === 1 ? "" : "es"} activa${num === 1 ? "" : "s"} (según filtros)${cupo}`;
  };

  /**
   * Idealista: el badge muestra marcas CRM (matriz) / contratados.
   * El cupo real (API + área privada) va en el title para no bloquear la lectura
   * al desmarcar; el gate de alta sigue usando cupo_usados en backend.
   */
  const badgeCupoPortal = (portal) => {
    const slug = String(portal?.slug || "").toLowerCase();
    const st = statsPortales[portal.id];
    const contratados =
      st?.anuncios_contratados ?? portal.anuncios_contratados ?? null;
    const marcasFiltro = contadoresPortales[portal.id] ?? 0;
    if (slug === "idealista") {
      const cupoUsados = st?.cupo_usados;
      const partners = st?.partners_published;
      const base = st?.ocupados_base ?? portal.anuncios_ocupados_base;
      const titleParts = [
        `${marcasFiltro.toLocaleString("es-ES")} activas en CRM` +
          (contratados != null ? ` · ${Number(contratados).toLocaleString("es-ES")} contratados` : ""),
      ];
      if (cupoUsados != null) {
        titleParts.push(
          `Cupo Idealista (API/área privada) ${Number(cupoUsados)}/${contratados != null ? Number(contratados) : "?"}`
        );
      }
      if (partners != null) titleParts.push(`Partners API: ${partners} publicados`);
      if (base != null && Number(base) > 0) {
        titleParts.push(`Ocupados fuera CRM (base): ${Number(base)}`);
      }
      if (cupoUsados != null && Number(cupoUsados) > marcasFiltro) {
        titleParts.push(
          "Hay anuncios en Idealista sin tick CRM: bajarlos en el portal o en Sooprema antes de marcar nuevas."
        );
      }
      return { count: marcasFiltro, contratados, title: titleParts.join(" · ") };
    }
    return {
      count: marcasFiltro,
      contratados,
      title: tituloContador(marcasFiltro, contratados),
    };
  };

  const ajustarContadorPortal = (portalId, newActivo) => {
    setContadoresPortales((prev) => {
      const cur = Number(prev[portalId]) || 0;
      let delta = 0;
      if (filtros.estado_portal === "inactivas") delta = newActivo ? -1 : 1;
      else delta = newActivo ? 1 : -1; // activas o todos
      return { ...prev, [portalId]: Math.max(0, cur + delta) };
    });
  };

  const refreshStatsPortales = useCallback(async () => {
    try {
      const res = await api.get("/portales/stats");
      const m = {};
      (res.data.data || []).forEach((s) => { m[s.id] = s; });
      setStatsPortales(m);
    } catch (err) {
      console.error("Error stats portales:", err);
    }
  }, []);

  const cargarFiltros = useCallback(async () => {
    try {
      const res = await api.get("/portales/filtros");
      setOpcionesFiltros(res.data);
    } catch (err) {
      console.error("Error cargando filtros:", err);
    }
  }, []);

  const cargarMatriz = useCallback(async (pagina = 1) => {
    const myRequestId = ++matrizRequestIdRef.current;
    setCargando(true);
    try {
      const limitN = MATRIZ_LIMITES.includes(Number(filtros.limit)) ? Number(filtros.limit) : 50;
      const params = new URLSearchParams({ page: pagina, limit: String(limitN) });
      // Siempre enviar solo_activas (true/false). Si se omite al estar en true por
      // un fallo de serialización, el backend devolvía también vendidas.
      params.set("solo_activas", filtros.solo_activas ? "true" : "false");
      Object.entries(filtros).forEach(([k, v]) => {
        if (k === "limit" || k === "solo_activas") return;
        if (k === "portal_ids") {
          if (Array.isArray(v) && v.length) params.set("portal_ids", v.join(","));
          return;
        }
        if (k === "estado_portal") {
          if (v && v !== "todos") params.set("estado_portal", v);
          return;
        }
        if (k === "exclusiva") {
          if (v && v !== "todas") params.set("exclusiva", v);
          return;
        }
        if (v && v !== "" && v !== false) params.set(k, v);
      });
      if (sortCol) {
        params.set("sort_col", sortCol);
        params.set("sort_dir", sortDir || "desc");
      }
      const res = await api.get(`/portales/propiedades/matriz?${params}`);
      // Respuesta obsoleta (p. ej. aún con referencia tras limpiar el filtro): no pisar.
      if (matrizRequestIdRef.current !== myRequestId) return;
      setPropiedades(res.data.data || []);
      setPortales(res.data.portales || []);
      setMatriz(res.data.matriz || {});
      setContadoresPortales(res.data.contadores || {});
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pagina);
    } catch (err) {
      if (matrizRequestIdRef.current !== myRequestId) return;
      console.error("Error cargando matriz:", err);
    } finally {
      if (matrizRequestIdRef.current === myRequestId) setCargando(false);
    }
  }, [filtros, sortCol, sortDir]);

  useEffect(() => { cargarFiltros(); }, [cargarFiltros]);
  useEffect(() => { refreshStatsPortales(); }, [refreshStatsPortales]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Primera carga: respeta página guardada en sessionStorage.
      if (skipResetPageRef.current) {
        skipResetPageRef.current = false;
        cargarMatriz(page);
        return;
      }
      cargarMatriz(1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // page a propósito fuera: paginación va por cargarMatriz(n) directo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, sortCol, sortDir, cargarMatriz]);

  // Al volver desde Configuración: recargar columnas (portales ON/OFF).
  const lastRefreshEpochRef = useRef(refreshEpoch);
  useEffect(() => {
    if (refreshEpoch === lastRefreshEpochRef.current) return;
    lastRefreshEpochRef.current = refreshEpoch;
    if (!refreshEpoch) return;
    refreshStatsPortales();
    cargarMatriz(page);
  }, [refreshEpoch, refreshStatsPortales, cargarMatriz, page]);

  const handleToggle = async (propId, portalId) => {
    if (bulkEnqueueRef.current) return;
    const key = `${propId}-${portalId}`;
    if (toggling === key) return;
    setToggling(key);
    const currentVal = !!(matriz[propId]?.[portalId] ?? matriz[String(propId)]?.[portalId]);
    const newVal = !currentVal;
    const portalRow = portales.find((p) => p.id === portalId || String(p.id) === String(portalId));

    // Check al instante; si cupo/sync falla, se revierte y avisa.
    setMatriz(prev => ({
      ...prev,
      [propId]: { ...(prev[propId] || {}), [portalId]: newVal }
    }));
    ajustarContadorPortal(portalId, newVal);

    try {
      const res = await api.post("/portales/propiedades/toggle", {
        propiedad_id: propId,
        portal_id: portalId,
        activo: newVal,
      });
      const confirmed = typeof res.data?.portal_activo === "boolean"
        ? res.data.portal_activo
        : newVal;
      setMatriz(prev => ({
        ...prev,
        [propId]: { ...(prev[propId] || {}), [portalId]: confirmed }
      }));
      if (confirmed !== newVal) {
        ajustarContadorPortal(portalId, confirmed);
      }
      refreshStatsPortales();
      if (res.data?.idealista?.warning && res.data.idealista.message) {
        await dialogs.alert(res.data.idealista.message);
      }
      if (!aplicarSyncFallosRespuesta(res.data)) {
        const pi = res.data?.pisos;
        if (pi?.ok === false) {
          registrarSyncFallos(
            [{
              ok: false,
              propiedad_id: propId,
              referencia: pi.referencia,
              error: pi.error || pi.message,
            }],
            "Pisos"
          );
        } else {
          cargarSyncFallos();
        }
      }
    } catch (err) {
      const data = err.response?.data || {};
      const portalActivo = typeof data.portal_activo === "boolean" ? data.portal_activo : currentVal;
      setMatriz(prev => ({
        ...prev,
        [propId]: { ...(prev[propId] || {}), [portalId]: portalActivo }
      }));
      if (portalActivo !== newVal) {
        ajustarContadorPortal(portalId, portalActivo);
      }
      console.error("Error toggling:", err);
      if (!aplicarSyncFallosRespuesta(data)) {
        const msg = textoErrorSync(
          data.fotocasa_error
          || data.pisos_error
          || data.idealista_error
          || data.error
          || err.message
          || "Error al sincronizar con el portal"
        );
        registrarSyncFallos(
          [{
            ok: false,
            propiedad_id: propId,
            referencia: data.referencia || propiedades.find((p) => p.id === propId)?.referencia,
            error: msg,
            code: data.code || null,
          }],
          portalRow?.nombre || portalRow?.slug || "Portal"
        );
        await alertSyncFalloUnaVez(msg);
      } else {
        const msg = textoErrorSync(
          data.fotocasa_error
          || data.pisos_error
          || data.idealista_error
          || data.error
          || err.message
          || "Error al sincronizar con el portal"
        );
        await alertSyncFalloUnaVez(msg);
      }
      refreshStatsPortales();
    } finally {
      setToggling(null);
    }
  };

  useEffect(() => {
    const bulkKey = bulkSync
      ? `${bulkSync.id}-${bulkSync.status}`
      : "";
    if (!bulkKey || !["pending", "running"].includes(String(bulkSync?.status || ""))) {
      if (!bulkSync) setBulkElapsedSec(0);
      return undefined;
    }
    const started = bulkSync.started_at || bulkSync.created_at;
    const t0 = started ? new Date(started).getTime() : Date.now();
    const tick = () => {
      setBulkElapsedSec(Math.max(0, Math.floor((Date.now() - t0) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [bulkSync?.id, bulkSync?.status, bulkSync?.started_at, bulkSync?.created_at]);

  // Poll job activo en servidor (también al volver a la pestaña).
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await api.get("/portales/sync-jobs/active");
        if (cancelled) return;
        const jobs = Array.isArray(res.data?.data) ? res.data.data : [];
        const job = jobs[0] || null;
        setBulkSync((prev) => {
          if (!job) {
            if (prev && ["pending", "running"].includes(String(prev.status || ""))) {
              // Job acabó: refrescar
              const finishedId = prev.id;
              if (bulkFinishedNotifiedRef.current !== finishedId) {
                bulkFinishedNotifiedRef.current = finishedId;
                setTimeout(() => {
                  refreshStatsPortales();
                  cargarMatriz(page);
                  cargarSyncFallos();
                }, 100);
              }
            }
            return null;
          }
          return {
            id: job.id,
            portal_id: job.portal_id,
            portalNombre: job.portal_nombre || job.portal_slug || "Portal",
            activar: Boolean(job.activo),
            total: Number(job.total) || 0,
            done: Number(job.done) || 0,
            ok: Number(job.ok) || 0,
            failed: Number(job.failed) || 0,
            status: job.status,
            started_at: job.started_at,
            created_at: job.created_at,
          };
        });
      } catch (err) {
        console.error("Error poll sync-jobs:", err);
      }
    };
    pull();
    const id = window.setInterval(pull, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [cargarMatriz, cargarSyncFallos, page, refreshStatsPortales]);

  const handleToggleColumna = async (portalId, activar) => {
    // Solo filas cuyo estado actual difiere del destino (✗ = solo las marcadas ON).
    // Antes se enviaban TODAS las de la página (p. ej. 481) y el job hacía DELETE masivo.
    const propIds = propiedades
      .filter((p) => Boolean(matriz[p.id]?.[portalId]) !== Boolean(activar))
      .map((p) => p.id);
    if (propIds.length === 0) {
      await dialogs.alert(
        activar
          ? "En esta página no hay propiedades sin marcar para activar."
          : "En esta página no hay propiedades marcadas para desactivar."
      );
      return;
    }
    const portalRow = portales.find((p) => p.id === portalId);
    const portalLabel = portalRow?.nombre || portalRow?.slug || "Portal";
    const slug = String(portalRow?.slug || "").toLowerCase();
    const ok = await dialogs.confirm(
      activar
        ? `¿Activar ${propIds.length} propiedad(es) en ${portalLabel} (solo las no marcadas de esta página)?`
        : `¿Desactivar ${propIds.length} propiedad(es) en ${portalLabel} (solo las marcadas de esta página)?`
    );
    if (!ok) return;
    if (bulkEnqueueRef.current) return;
    bulkEnqueueRef.current = true;

    const esperaConfirmacion = ["fotocasa", "pisos", "idealista"].includes(slug);

    // Feeds: marcar ya. APIs: no pintar checks hasta que el job confirme cada ficha.
    if (!esperaConfirmacion) {
      let delta = 0;
      propIds.forEach((pid) => {
        const was = !!matriz[pid]?.[portalId];
        if (was !== activar) {
          if (filtros.estado_portal === "inactivas") delta += activar ? -1 : 1;
          else delta += activar ? 1 : -1;
        }
      });
      const updatedMatriz = { ...matriz };
      propIds.forEach(pid => {
        if (!updatedMatriz[pid]) updatedMatriz[pid] = {};
        updatedMatriz[pid][portalId] = activar;
      });
      setMatriz(updatedMatriz);
      if (delta) {
        setContadoresPortales((prev) => ({
          ...prev,
          [portalId]: Math.max(0, (Number(prev[portalId]) || 0) + delta),
        }));
      }
    }

    try {
      const res = await api.post(
        "/portales/propiedades/bulk",
        {
          propiedad_ids: propIds,
          portal_id: portalId,
          activo: activar,
        },
        { timeout: 60000 }
      );
      const job = res.data?.job;
      if (job) {
        bulkFinishedNotifiedRef.current = null;
        setBulkSync({
          id: job.id,
          portal_id: job.portal_id,
          portalNombre: job.portal_nombre || portalLabel,
          activar: Boolean(job.activo),
          total: Number(job.total) || propIds.length,
          done: Number(job.done) || 0,
          ok: Number(job.ok) || 0,
          failed: Number(job.failed) || 0,
          status: job.status || "pending",
          started_at: job.started_at,
          created_at: job.created_at,
        });
      } else {
        refreshStatsPortales();
        cargarMatriz(page);
      }
    } catch (err) {
      console.error("Error enqueue bulk:", err);
      cargarMatriz(page);
      const msg = textoErrorSync(err.response?.data?.error || err.message);
      await dialogs.alert(`${msg}\n\nNo se pudo encolar la sincronización.`);
    } finally {
      bulkEnqueueRef.current = false;
    }
  };

  const cancelarBulkSync = async () => {
    if (!bulkSync?.id) return;
    try {
      await api.post(`/portales/sync-jobs/${bulkSync.id}/cancel`);
      setBulkSync(null);
      refreshStatsPortales();
      cargarMatriz(page);
      cargarSyncFallos();
    } catch (err) {
      await dialogs.alert(textoErrorSync(err.response?.data?.error || err.message));
    }
  };

  const togglePortalFiltro = (portalId) => {
    setFiltros((prev) => {
      const pid = Number(portalId);
      const has = (prev.portal_ids || []).some((id) => Number(id) === pid);
      const ids = has
        ? prev.portal_ids.filter((id) => Number(id) !== pid)
        : [...(prev.portal_ids || []), portalId];
      return { ...prev, portal_ids: ids };
    });
  };

  const limpiarFiltros = () => {
    setFiltros({
      ...MATRIZ_FILTROS_DEFAULT,
      limit: filtros.limit || 50,
      portal_ids: portalesFiltroOpciones.map((p) => p.id),
    });
    setHighlightPortal(null);
  };

  const hayFiltrosActivos = Object.entries(filtros).some(([k, v]) => {
    if (k === "limit") return false;
    // Por defecto está activo; solo cuenta como filtro si el usuario lo desactiva
    if (k === "solo_activas") return v === false;
    if (k === "estado_portal") return v && v !== "todos";
    if (k === "exclusiva") return v && v !== "todas";
    if (k === "portal_ids") return Array.isArray(v) && v.length > 0;
    return v !== "";
  });

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { handleDragEnd(); return; }
    const visible = [...portalesVisibles];
    const [moved] = visible.splice(dragIdx, 1);
    visible.splice(targetIdx, 0, moved);
    const visibleIds = new Set(visible.map((p) => Number(p.id)));
    const hidden = (portales || []).filter((p) => !visibleIds.has(Number(p.id)));
    const reordered = [...visible, ...hidden];
    setPortales(reordered);
    handleDragEnd();
    try {
      await api.put("/portales/reordenar", { orden: reordered.map((p) => p.id) });
    } catch (err) {
      console.error("Error reordenando:", err);
    }
  };

  const imgBase = window.location.hostname === "localhost" ? "http://localhost:4000" : "https://vitalcasa-crm-production.up.railway.app";

  const absFotoUrl = (u) => {
    if (!u) return null;
    const s = typeof u === "string" ? u : String(u.url || u.src || "").trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return `${imgBase}${s.startsWith("/") ? "" : "/"}${s}`;
  };

  const abrirVisor = (fotos) => {
    const list = (Array.isArray(fotos) ? fotos : []).map(absFotoUrl).filter(Boolean);
    if (!list.length) return;
    setVisor({ abierto: true, fotos: list, indice: 0 });
  };
  const cerrarVisor = () => setVisor({ abierto: false, fotos: [], indice: 0 });
  const visorAnterior = () => {
    setVisor((v) => ({ ...v, indice: v.indice > 0 ? v.indice - 1 : v.fotos.length - 1 }));
  };
  const visorSiguiente = () => {
    setVisor((v) => ({ ...v, indice: v.indice < v.fotos.length - 1 ? v.indice + 1 : 0 }));
  };

  const abrirVisorPropiedad = async (prop) => {
    try {
      const res = await api.get(`/propiedades/${prop.id}/fotos`);
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const urls = rows.map(absFotoUrl).filter(Boolean);
      if (urls.length) {
        abrirVisor(urls);
        return;
      }
    } catch (err) {
      console.error("Error cargando fotos:", err);
    }
    const one = absFotoUrl(prop.foto);
    if (one) abrirVisor([one]);
  };

  const abrirVistaPrevia = async (prop) => {
    if (!prop?.id) return;
    setFicha({ abierta: true, pack: null, cargando: true });
    try {
      const datos = await cargarDatosFichaResumen(prop.id);
      setFicha({
        abierta: true,
        pack: { p: { ...prop, ...datos }, d: datos },
        cargando: false,
      });
    } catch (err) {
      console.error("Error vista previa Portales:", err);
      setFicha({ abierta: false, pack: null, cargando: false });
      await dialogs.alert(
        `No se pudo abrir la vista previa: ${err.response?.data?.error || err.message || err}`
      );
    }
  };

  const inputStyle = { background: t.inputBg, border: `2px solid ${t.inputBorder}`, color: t.textPrimary };
  const thumbW = 135;
  /** Bajo el header sticky de AppLayout; la matriz se fija y la thead no se pierde al scrollear filas. */
  const MATRIZ_STICKY_TOP_PX = 52;
  const theadBg = isDark ? t.bgPanel : "#f1f5f9";
  const thumbH = 102;

  return (
    <div>
      <div className="rounded-xl mb-4" style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
        <button onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold"
          style={{ color: t.textSecondary }}>
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span>Filtros</span>
            {hayFiltrosActivos && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{ background: t.accentBlue }}>
                activos
              </span>
            )}
          </div>
          <span style={{ color: t.textMuted }}>{filtrosAbiertos ? "▲" : "▼"}</span>
        </button>

        {filtrosAbiertos && (
          <div className="px-4 pb-4 pt-1">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Referencia</label>
                <input value={filtros.referencia} onChange={e => setFiltros({ ...filtros, referencia: e.target.value })}
                  placeholder="3478-C..."
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Tipo</label>
                <select value={filtros.tipo} onChange={e => setFiltros({ ...filtros, tipo: e.target.value })}
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle}>
                  <option value="">Todos</option>
                  {opcionesFiltros.tipos.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Población</label>
                <select value={filtros.poblacion} onChange={e => setFiltros({ ...filtros, poblacion: e.target.value })}
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle}>
                  <option value="">Todas</option>
                  {opcionesFiltros.poblaciones.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Operación</label>
                <select value={filtros.operacion} onChange={e => setFiltros({ ...filtros, operacion: e.target.value })}
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle}>
                  <option value="">Todas</option>
                  {opcionesFiltros.operaciones.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Precio desde</label>
                <input value={filtros.precio_min} onChange={e => setFiltros({ ...filtros, precio_min: e.target.value })}
                  type="number" placeholder="0"
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Precio hasta</label>
                <input value={filtros.precio_max} onChange={e => setFiltros({ ...filtros, precio_max: e.target.value })}
                  type="number" placeholder="∞"
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Hab. mín.</label>
                <input value={filtros.habitaciones_min} onChange={e => setFiltros({ ...filtros, habitaciones_min: e.target.value })}
                  type="number" placeholder="0" min="0"
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Exclusivas</label>
                <select value={filtros.exclusiva} onChange={e => setFiltros({ ...filtros, exclusiva: e.target.value })}
                  className="w-full px-2 py-1.5 rounded-lg text-xs" style={inputStyle}>
                  <option value="todas">Todas</option>
                  <option value="si">Solo exclusivas</option>
                  <option value="no">Sin exclusiva</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-2 mt-3">
              <div className="flex-1 min-w-0">
                <label className="block text-[10px] font-bold uppercase mb-1.5" style={{ color: t.textMuted }}>
                  Portales
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {portalesFiltroOpciones.map((p) => {
                    const sel = filtros.portal_ids.some((id) => Number(id) === Number(p.id));
                    const color = p.color || t.accentBlue;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePortalFiltro(p.id)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
                        style={{
                          color: sel ? "#fff" : color,
                          background: sel ? color : (isDark ? `${color}22` : `${color}18`),
                          border: `1px solid ${color}`,
                        }}
                        title={sel ? `Quitar filtro ${p.nombre}` : `Filtrar por ${p.nombre}`}
                      >
                        {p.nombre}
                      </button>
                    );
                  })}
                  {!portalesFiltroOpciones.length && (
                    <span className="text-xs" style={{ color: t.textMuted }}>
                      {cargando ? "Cargando…" : "Activa portales en Configuración"}
                    </span>
                  )}
                </div>
              </div>
              <div className="shrink-0 w-[7.5rem]">
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Estado</label>
                <select
                  value={filtros.estado_portal}
                  onChange={(e) => setFiltros({ ...filtros, estado_portal: e.target.value })}
                  className="w-full px-1.5 py-1 rounded-lg text-xs"
                  style={inputStyle}
                  title="Estado en portal"
                >
                  <option value="todos">Todas</option>
                  <option value="activas">Activas</option>
                  <option value="inactivas">Inactivas</option>
                </select>
              </div>
              <div className="shrink-0 w-[4.5rem]">
                <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: t.textMuted }}>Líneas</label>
                <select
                  value={filtros.limit}
                  onChange={(e) => setFiltros({ ...filtros, limit: Number(e.target.value) })}
                  className="w-full px-1.5 py-1 rounded-lg text-xs"
                  style={inputStyle}
                  title="Líneas a mostrar"
                >
                  {MATRIZ_LIMITES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: `1px solid ${t.border}` }}>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: t.textSecondary }}>
                <input type="checkbox" checked={filtros.solo_activas} onChange={e => setFiltros({ ...filtros, solo_activas: e.target.checked })} className="rounded" />
                Solo disponibles
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs" style={{ color: t.textMuted }}>{total} propiedades encontradas</span>
                {hayFiltrosActivos && (
                  <button onClick={limpiarFiltros} className="text-xs px-2 py-1 rounded"
                    style={{ color: t.accentRed }}>Limpiar filtros</button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs mb-3" style={{ color: t.textMuted }}>
        Celdas: clic en ✓/✗ para publicar o quitar esa propiedad. Encima de cada logo, ✓/✗ aplica a toda la página.
        Los portales de la matriz se activan en <strong style={{ color: t.textSecondary }}>Configuración</strong> (columna Estado).
      </p>

      <div
        id="portales-panel-fallos"
        className="mb-3 rounded-xl overflow-hidden"
        style={{
          border: `1px solid ${syncFallosVisibles.length ? (t.accentRed || "#ef4444") : t.border}`,
          background: t.bgCard,
        }}
      >
          <div className="px-4 py-2.5 flex items-center justify-between gap-2" style={{ borderBottom: syncFallosAbiertos ? `1px solid ${t.border}` : "none" }}>
            <button
              type="button"
              onClick={() => setSyncFallosAbiertos((v) => !v)}
              className="text-sm font-semibold flex items-center gap-2"
              style={{ color: syncFallosVisibles.length ? (t.accentRed || "#ef4444") : t.textSecondary }}
            >
              <span>{syncFallosAbiertos ? "▼" : "▶"}</span>
              Fallos de sincronización ({syncFallosVisibles.length})
            </button>
            <div className="flex items-center gap-2">
              {(!slugsFallosFiltro.length || slugsFallosFiltro.includes("fotocasa")) && (
              <button
                type="button"
                className="text-xs px-2 py-1 rounded"
                disabled={syncFallosCargando}
                style={{ color: t.accentBlue, border: `1px solid ${t.border}`, opacity: syncFallosCargando ? 0.6 : 1 }}
                onClick={auditarSyncFallos}
                title="Recalcula fallos: activas en CRM sin remoto + bloqueos de mapper (p. ej. sin m²)"
              >
                {syncFallosCargando ? "Auditando…" : "Auditar Fotocasa"}
              </button>
              )}
              {syncFallosVisibles.length > 0 && (
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: t.accentBlue, border: `1px solid ${t.border}` }}
                  onClick={async () => {
                    const text = syncFallosVisibles
                      .map((f) => `${f.referencia}\t${f.portal}\t${f.error}`)
                      .join("\n");
                    try {
                      await navigator.clipboard.writeText(text);
                      await dialogs.alert("Listado copiado");
                    } catch {
                      await dialogs.alert("No se pudo copiar");
                    }
                  }}
                >
                  Copiar
                </button>
              )}
              {syncFallosVisibles.length > 0 && (
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: t.textMuted, border: `1px solid ${t.border}` }}
                  onClick={limpiarSyncFallos}
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
          {syncFallosAbiertos && (
            <div className="max-h-56 overflow-auto">
              {syncFallosVisibles.length === 0 ? (
                <p className="px-4 py-3 text-xs" style={{ color: t.textMuted }}>
                  {slugsFallosFiltro.length
                    ? "No hay fallos de sincronización para el portal filtrado."
                    : "Sin fallos. La columna Portal indica de cuál es cada error si hay varios portales."}
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: isDark ? t.bgPanel : "#f8fafc" }}>
                      <th className="text-left px-3 py-1.5 font-bold whitespace-nowrap" style={{ color: t.textMuted, width: "1%", minWidth: "5.5rem" }}>Ref.</th>
                      <th className="text-left px-3 py-1.5 font-bold whitespace-nowrap" style={{ color: t.textMuted, width: "1%" }}>Portal</th>
                      <th className="text-left px-3 py-1.5 font-bold" style={{ color: t.textMuted }}>Motivo</th>
                      <th className="text-left px-3 py-1.5 font-bold whitespace-nowrap" style={{ color: t.textMuted, width: "1%" }}>Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncFallosVisibles.map((f) => (
                      <tr
                        key={f.id}
                        style={{
                          borderTop: `1px solid ${t.border}`,
                          cursor: f.propiedad_id ? "pointer" : "default",
                        }}
                        title={f.propiedad_id ? "Abrir propiedad y corregir el campo del error" : undefined}
                        onClick={() => { if (f.propiedad_id) abrirFalloEnPropiedad(f); }}
                        onMouseEnter={(e) => {
                          if (f.propiedad_id) e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight;
                        }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td className="px-3 py-1.5 font-mono font-semibold whitespace-nowrap" style={{ color: isDark ? "#fbbf24" : t.accentBlue, width: "1%", minWidth: "5.5rem" }}>{f.referencia}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap" style={{ color: t.textSecondary }}>{f.portal}</td>
                        <td className="px-3 py-1.5 break-words" style={{ color: t.textPrimary }}>{f.error}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap" style={{ color: t.textMuted }}>
                          {new Date(f.at).toLocaleString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

      {bulkSync && ["pending", "running"].includes(String(bulkSync.status || "")) && (
        <div
          className="mb-3 rounded-xl px-4 py-3 flex items-center gap-3 fixed left-3 right-3 sm:left-auto sm:right-6 sm:w-[min(28rem,calc(100vw-1.5rem))] top-16 z-[60]"
          style={{
            background: isDark ? "#1e3a5f" : "#eff6ff",
            border: `1px solid ${t.accentBlue || "#3b82f6"}`,
            boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.35)" : "0 4px 14px rgba(37,99,235,0.15)",
          }}
          role="status"
          aria-live="polite"
        >
          <span
            className="inline-block w-5 h-5 rounded-full border-2 border-t-transparent animate-spin shrink-0"
            style={{ borderColor: `${t.accentBlue || "#3b82f6"} transparent transparent transparent` }}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold" style={{ color: isDark ? "#bfdbfe" : "#1e40af" }}>
              {bulkSync.activar ? "Publicando" : "Dando de baja"} en {bulkSync.portalNombre}… (segundo plano)
            </p>
            <p className="text-xs mt-0.5" style={{ color: isDark ? "#93c5fd" : "#1d4ed8" }}>
              {(Number(bulkSync.done) || 0).toLocaleString("es-ES")} / {(Number(bulkSync.total) || 0).toLocaleString("es-ES")}
              {bulkSync.failed ? ` · ${bulkSync.failed} fallos` : ""}
              {" "}· Puedes cambiar de página o salir; sigue en el servidor
              {bulkElapsedSec > 0 ? ` · ${bulkElapsedSec}s` : ""}
            </p>
            <div
              className="mt-2 h-1.5 rounded-full overflow-hidden"
              style={{ background: isDark ? "rgba(255,255,255,0.12)" : "rgba(37,99,235,0.15)" }}
            >
              <div
                className="h-full rounded-full transition-[width] duration-300 ease-out"
                style={{
                  width: `${Math.max(2, Math.min(100, Math.round(((Number(bulkSync.done) || 0) / Math.max(1, Number(bulkSync.total) || 1)) * 100)))}%`,
                  background: t.accentBlue || "#3b82f6",
                }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={cancelarBulkSync}
            className="shrink-0 text-xs px-2.5 py-1.5 rounded-lg font-medium"
            style={{
              border: `1px solid ${isDark ? "#93c5fd55" : "#93c5fd"}`,
              color: isDark ? "#bfdbfe" : "#1e40af",
              background: "transparent",
            }}
            title="Cancelar pendientes"
          >
            Cancelar
          </button>
        </div>
      )}

      {cargando ? (
        <div className="text-center py-16" style={{ color: t.textMuted }}>Cargando propiedades...</div>
      ) : portales.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
          <p className="text-sm font-medium mb-1" style={{ color: t.textSecondary }}>No hay portales activos</p>
          <p className="text-xs" style={{ color: t.textMuted }}>Se muestran solo Fotocasa, Idealista, Pisos.com e Indomio</p>
        </div>
      ) : propiedades.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
          <p className="text-sm" style={{ color: t.textMuted }}>No se encontraron propiedades con los filtros actuales</p>
        </div>
      ) : (
        <div
          className="rounded-xl flex flex-col"
          style={{
            border: `1px solid ${t.border}`,
            position: "sticky",
            top: MATRIZ_STICKY_TOP_PX,
            zIndex: 20,
            maxHeight: `calc(100vh - ${MATRIZ_STICKY_TOP_PX}px)`,
          }}
        >
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: theadBg, borderBottom: `2px solid ${t.border}` }}>
                  <th
                    className="px-0 py-2.5 text-xs font-bold uppercase sticky z-30"
                    style={{
                      top: 0,
                      left: 0,
                      color: t.textMuted,
                      background: theadBg,
                      width: thumbW,
                      borderRight: `1px solid ${t.border}`,
                    }}
                  >
                  </th>
                  <th
                    className="text-left px-3 py-2.5 text-xs font-bold uppercase select-none sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 60, cursor: "pointer", borderRight: `1px solid ${t.border}`, background: theadBg }}
                    onClick={() => toggleSortMatriz("referencia")}
                    title="Ordenar por referencia"
                  >
                    Ref
                  </th>
                  <th
                    className="text-left px-3 py-2.5 text-xs font-bold uppercase select-none sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 72, cursor: "pointer", borderRight: `1px solid ${t.border}`, background: theadBg }}
                    onClick={() => toggleSortMatriz("fecha_alta")}
                    title="Ordenar por fecha de alta"
                  >
                    Fecha alta
                  </th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 90, borderRight: `1px solid ${t.border}`, background: theadBg }}>Tipo</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 50, borderRight: `1px solid ${t.border}`, background: theadBg }}>Población</th>
                  <th
                    className="text-right px-3 py-2.5 text-xs font-bold uppercase select-none sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 80, cursor: "pointer", borderRight: `1px solid ${t.border}`, background: theadBg }}
                    onClick={() => toggleSortMatriz("precio")}
                    title="Ordenar por precio"
                  >
                    Precio
                  </th>
                  <th className="text-center px-3 py-2.5 text-xs font-bold uppercase sticky top-0 z-20"
                    style={{ color: t.textMuted, minWidth: 40, borderRight: `1px solid ${t.border}`, background: theadBg }}>Hab</th>
                  {portalesVisibles.map((portal, idx) => {
                    const isHighlighted = highlightPortal === portal.id;
                    const isLast = idx === portalesVisibles.length - 1;
                    const badgeCupo = badgeCupoPortal(portal);
                    const colBg = isHighlighted || dragOverIdx === idx
                      ? (isDark ? "#1e3a5f" : "#dbeafe")
                      : theadBg;
                    return (
                    <th
                      key={portal.id}
                      className="text-center px-1 py-2.5 sticky top-0 z-20"
                      style={{
                        minWidth: 72, opacity: dragIdx === idx ? 0.4 : 1,
                        background: colBg,
                        borderRight: isLast ? "none" : `1px solid ${t.border}`,
                      }}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      onDrop={() => handleDrop(idx)}>
                      <div className="flex flex-col items-center gap-1.5 cursor-grab active:cursor-grabbing"
                        onClick={() => setHighlightPortal(isHighlighted ? null : portal.id)}
                        title={portal.nombre}>
                        {portal.logo_url ? (
                          <img
                            src={portal.logo_url}
                            alt={portal.nombre || ""}
                            className="rounded-full object-cover"
                            style={{
                              width: 42,
                              height: 42,
                              boxShadow: isHighlighted ? `0 0 0 3px ${portal.color || t.accentBlue}40` : "none",
                              background: "#fff",
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-full flex items-center justify-center text-white text-sm font-bold"
                            style={{
                              width: 42,
                              height: 42,
                              background: portal.color || t.accentBlue,
                              boxShadow: isHighlighted ? `0 0 0 3px ${portal.color || t.accentBlue}40` : "none",
                            }}
                          >
                            {portal.nombre?.charAt(0)}
                          </div>
                        )}
                        <PortalPropCountBadge
                          large
                          count={badgeCupo.count}
                          contratados={badgeCupo.contratados}
                          title={badgeCupo.title}
                          portalColor={portal.color}
                          isDark={isDark}
                          accentFallback={t.accentBlue}
                        />
                        <div className="flex gap-0.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleToggleColumna(portal.id, true); }}
                            className="text-[8px] px-1 rounded"
                            style={{ color: t.accentGreen, cursor: "pointer" }}
                            title={bulkSync && Number(bulkSync.portal_id) === Number(portal.id) ? "Añadir esta página a la cola en curso" : "Activar todos"}
                          >✓</button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleToggleColumna(portal.id, false); }}
                            className="text-[8px] px-1 rounded"
                            style={{ color: t.accentRed, cursor: "pointer" }}
                            title="Desactivar todos"
                          >✗</button>
                        </div>
                      </div>
                    </th>
                  );})}
                </tr>
              </thead>
              <tbody>
                {propiedades.map((prop) => (
                  <tr key={prop.id}
                    style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.bgCard; }}>
                    <td className="px-0 py-0 sticky left-0 z-10" style={{ background: "inherit", width: thumbW, borderRight: `1px solid ${t.border}` }}>
                      {prop.foto ? (
                        <img
                          src={prop.foto.startsWith("http") ? prop.foto : imgBase + prop.foto}
                          alt=""
                          className="block object-cover cursor-pointer"
                          style={{ width: thumbW, height: thumbH }}
                          title="Ver fotos"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirVisorPropiedad(prop);
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center text-[10px]" style={{ width: thumbW, height: thumbH, color: t.textMuted, background: isDark ? t.bgPanel : "#e2e8f0" }}>—</div>
                      )}
                    </td>
                    <td className="px-3 py-2" style={{ borderRight: `1px solid ${t.border}` }}>
                      <button
                        type="button"
                        className="text-xs font-bold font-mono hover:underline"
                        style={{ color: isDark ? "#fbbf24" : t.accentBlue, background: "none", border: 0, padding: 0, cursor: "pointer" }}
                        title="Vista previa"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirVistaPrevia(prop);
                        }}
                      >
                        {prop.referencia}
                      </button>
                    </td>
                    <td className="px-3 py-2" style={{ borderRight: `1px solid ${t.border}` }}>
                      <span className="text-xs tabular-nums" style={{ color: t.textSecondary }}>
                        {formatFechaAlta(prop.fecha_alta)}
                      </span>
                    </td>
                    <td className="px-3 py-2" style={{ borderRight: `1px solid ${t.border}` }}>
                      <span className="text-xs" style={{ color: t.textPrimary }}>{prop.tipo || "—"}</span>
                    </td>
                    <td className="px-3 py-2" style={{ borderRight: `1px solid ${t.border}` }}>
                      <span className="text-xs truncate block max-w-[4.5rem]" style={{ color: t.textPrimary }} title={prop.poblacion || ""}>
                        {prop.poblacion || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right" style={{ borderRight: `1px solid ${t.border}` }}>
                      <span className="text-xs font-medium" style={{ color: t.textPrimary }}>
                        {prop.precio_venta ? Number(prop.precio_venta).toLocaleString("es-ES") + "€" : "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center" style={{ borderRight: `1px solid ${t.border}` }}>
                      <span className="text-xs" style={{ color: t.textPrimary }}>{prop.num_dormitorios || "—"}</span>
                    </td>
                    {portalesVisibles.map((portal, idx) => {
                      const activo = matriz[prop.id]?.[portal.id] || false;
                      const isToggling = toggling === `${prop.id}-${portal.id}`;
                      const isHL = highlightPortal === portal.id;
                      const isLast = idx === portalesVisibles.length - 1;
                      const bulkBusy = false; // sync en servidor; celda individual sigue disponible
                      return (
                        <td key={portal.id} className="px-1 py-2 text-center"
                          style={{
                            background: isHL ? (isDark ? "#1e3a5f20" : "#dbeafe60") : "transparent",
                            borderRight: isLast ? "none" : `1px solid ${t.border}`,
                          }}>
                          <button
                            onClick={() => handleToggle(prop.id, portal.id)}
                            disabled={isToggling || bulkBusy}
                            className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto transition-all"
                            title={activo ? `Quitar de ${portal.nombre}` : `Publicar en ${portal.nombre}`}
                            style={{
                              background: activo ? "#10b98120" : (isDark ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                              border: `2px solid ${activo ? "#10b981" : (isDark ? t.border : "#cbd5e1")}`,
                              opacity: (isToggling || bulkBusy) ? 0.4 : 1,
                              cursor: (isToggling || bulkBusy) ? "not-allowed" : "pointer",
                            }}>
                            {activo ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#64748b" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              className="flex items-center justify-center gap-3 shrink-0 px-3 py-2.5"
              style={{
                borderTop: `1px solid ${t.border}`,
                background: t.bgCard,
              }}
            >
              <button
                type="button"
                onClick={() => cargarMatriz(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ border: `1px solid ${t.border}`, color: page <= 1 ? t.textMuted : t.textSecondary, background: t.bgCard, opacity: page <= 1 ? 0.5 : 1 }}
              >
                ‹ Anterior
              </button>
              <span className="text-sm" style={{ color: t.textMuted }}>
                Página {page} de {totalPages}
                {bulkSync ? " · sync en segundo plano" : ""}
              </span>
              <button
                type="button"
                onClick={() => cargarMatriz(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ border: `1px solid ${t.border}`, color: page >= totalPages ? t.textMuted : t.textSecondary, background: t.bgCard, opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Siguiente ›
              </button>
            </div>
          )}
        </div>
      )}

      {ficha.abierta ? (
        <FichaResumenPanel
          p={ficha.pack?.p}
          d={ficha.pack?.d}
          cargando={ficha.cargando}
          onClose={() => setFicha({ abierta: false, pack: null, cargando: false })}
        />
      ) : null}

      {visor.abierto ? (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={cerrarVisor}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={cerrarVisor}
            className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 z-10"
          >
            ✕
          </button>

          {visor.fotos.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                visorAnterior();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
            >
              ‹
            </button>
          ) : null}

          <img
            src={visor.fotos[visor.indice]}
            alt=""
            className="max-w-[98vw] max-h-[96vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {visor.fotos.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                visorSiguiente();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl font-light hover:text-gray-300 z-10"
            >
              ›
            </button>
          ) : null}

          <div className="absolute bottom-4 text-white text-sm">
            {visor.indice + 1} / {visor.fotos.length}
          </div>
        </div>
      ) : null}
    </div>
  );
}


// ── Página principal ───────────────────────────────────────────────────────

export default function Portales() {
  const { theme } = useTheme();
  const t = theme.colors;
  const location = useLocation();
  const restore = location.state && typeof location.state === "object" ? location.state : null;
  const [tab, setTab] = useState(restore?.tab === "config" ? "config" : "matriz");
  const [filtroPortal, setFiltroPortal] = useState(null);
  const openFallosInicial = !!restore?.openFallos;

  // Al entrar / volver: arriba, salvo si hay que restaurar el panel Fallos (scroll lo hace SeccionMatriz).
  useEffect(() => {
    if (openFallosInicial) return undefined;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const t1 = window.setTimeout(() => window.scrollTo(0, 0), 0);
    const t2 = window.setTimeout(() => window.scrollTo(0, 0), 100);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [openFallosInicial]);

  const tabs = [
    { id: "config", label: "Configuración de Portales", icon: "⚙️" },
    { id: "matriz", label: "Propiedades × Portales", icon: "📊" },
  ];

  const [matrizMontada, setMatrizMontada] = useState(
    () => tab === "matriz" || !!restore?.tab
  );
  const [matrizRefreshEpoch, setMatrizRefreshEpoch] = useState(0);

  useEffect(() => {
    if (tab === "matriz") {
      setMatrizMontada(true);
      setMatrizRefreshEpoch((n) => n + 1);
    }
  }, [tab]);

  const irAMatriz = (portalId) => {
    setFiltroPortal(portalId || null);
    setTab("matriz");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  return (
    <div className="min-h-screen" style={{ background: t.bgApp }}>
      <header className="px-6 py-4 flex items-center justify-between"
        style={{ background: t.bgCard, borderBottom: `1px solid ${t.border}` }}>
        <div>
          <h1 className="font-bold text-lg" style={{ color: t.textPrimary }}>Portales Inmobiliarios</h1>
          <p className="text-xs" style={{ color: t.textMuted }}>Gestión de feeds, APIs y publicación en portales</p>
        </div>
      </header>

      <div className="px-6 pt-4">
        <div className="flex gap-1 mb-4" style={{ borderBottom: `2px solid ${t.border}` }}>
          {tabs.map(tabItem => {
            const isMatriz = tabItem.id === "matriz";
            const green = t.accentGreen || "#16a34a";
            const activeColor = isMatriz ? green : t.accentBlue;
            return (
            <button key={tabItem.id}
              onClick={() => { setTab(tabItem.id); }}
              className="px-4 py-2.5 text-sm font-semibold transition-colors relative"
              style={{
                color: isMatriz ? green : (tab === tabItem.id ? t.accentBlue : t.textMuted),
                background: "transparent",
              }}>
              <span className="mr-1.5">{tabItem.icon}</span>
              {tabItem.label}
              {tab === tabItem.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: activeColor }} />
              )}
            </button>
            );
          })}
        </div>

        {tab === "config" && <SeccionConfig theme={theme} onIrAMatriz={irAMatriz} />}
        {matrizMontada && (
          <div style={{ display: tab === "matriz" ? "block" : "none" }}>
            <SeccionMatriz
              theme={theme}
              filtroPortalInicial={filtroPortal}
              openFallosInicial={openFallosInicial}
              refreshEpoch={matrizRefreshEpoch}
            />
          </div>
        )}
      </div>
    </div>
  );
}
